(function () {
  'use strict';

  /* =========================================================
     Biofield Assessment — Biological Age Quiz
     Single-question flow with micro-feedback and GSAP
     ========================================================= */

  var CATEGORIES = [
    {
      id: 'cognitive', label: 'Cognitive Performance', emoji: '\uD83E\uDDE0',
      description: 'Memory, focus, and mental processing speed',
      questions: [
        {
          id: 'brain_fog', text: 'How often do you experience brain fog or mental fatigue?',
          options: [
            { label: 'Daily', desc: "It's a constant battle", value: 0 },
            { label: 'A few times a week', desc: 'Comes and goes', value: 1 },
            { label: 'Rarely', desc: 'Once in a while', value: 2 },
            { label: 'Almost never', desc: 'My mind stays sharp', value: 3 }
          ],
          insights: [
            'Daily brain fog is often linked to chronic inflammation and poor sleep \u2014 both accelerate biological aging.',
            'Intermittent fog can signal cortisol imbalance. Your brain is fighting harder than it should.',
            "That's a good sign. Occasional fog is normal \u2014 consistent clarity takes targeted support.",
            'Exceptional. Sustained mental clarity is one of the strongest markers of cognitive youth.'
          ]
        },
        {
          id: 'memory', text: 'How would you rate your short-term memory?',
          options: [
            { label: 'Frequently forget things', desc: 'Names, tasks, where I put things', value: 0 },
            { label: 'Sometimes struggle', desc: 'Occasional lapses', value: 1 },
            { label: 'Generally sharp', desc: 'Rarely forget important things', value: 2 },
            { label: 'Exceptional', desc: 'I remember details others miss', value: 3 }
          ],
          insights: [
            'Memory decline is one of the earliest measurable signs of accelerated brain aging. This is reversible with the right approach.',
            'Occasional lapses are common after 35 \u2014 but they don\u2019t have to be your normal.',
            'Solid. Your hippocampus is holding up well \u2014 consistent sleep and targeted nutrients keep it that way.',
            'Outstanding memory retention. This suggests strong neuroplasticity and efficient neural pathways.'
          ]
        },
        {
          id: 'focus_switching', text: 'How quickly can you shift focus between tasks?',
          options: [
            { label: 'I really struggle', desc: 'Takes me a long time to refocus', value: 0 },
            { label: 'Takes a moment', desc: 'Need a few minutes to reset', value: 1 },
            { label: 'Pretty quick', desc: 'Can shift without much delay', value: 2 },
            { label: 'Near-instant', desc: 'I transition seamlessly', value: 3 }
          ],
          insights: [
            'Difficulty switching tasks reflects prefrontal cortex strain \u2014 often driven by chronic stress or nutrient deficiency.',
            'A short refocus period is normal, but optimizing it can dramatically improve your cognitive throughput.',
            'Good task-switching ability. Your executive function is performing well.',
            'Near-instant focus switching is rare and reflects peak cognitive performance. Your brain is operating efficiently.'
          ]
        }
      ]
    },
    {
      id: 'inflammation', label: 'Inflammation & Recovery', emoji: '\uD83D\uDD25',
      description: 'How your body handles damage, repair, and chronic stress',
      questions: [
        {
          id: 'recovery_time', text: 'How long does it take you to recover from physical exertion?',
          options: [
            { label: 'Days', desc: 'I feel it for a while', value: 0 },
            { label: 'About 24 hours', desc: "Next day I'm still sore", value: 1 },
            { label: 'Same day', desc: 'Bounce back quickly', value: 2 },
            { label: 'Barely notice', desc: 'Ready to go again soon', value: 3 }
          ],
          insights: [
            'Extended recovery is a key biomarker for systemic inflammation. Your body\u2019s repair systems are working overtime.',
            'Next-day soreness suggests your inflammatory response is slightly elevated. This is addressable.',
            'Same-day recovery indicates good inflammatory balance. Your body\u2019s repair mechanisms are efficient.',
            'Rapid recovery is a hallmark of low systemic inflammation \u2014 one of the strongest predictors of longevity.'
          ]
        },
        {
          id: 'joint_stiffness', text: 'Do you experience unexplained joint stiffness or body soreness?',
          options: [
            { label: 'Daily', desc: "It's just part of my life now", value: 0 },
            { label: 'Frequently', desc: 'Several times a week', value: 1 },
            { label: 'Occasionally', desc: 'After specific activities', value: 2 },
            { label: 'Rarely', desc: 'Almost never without cause', value: 3 }
          ],
          insights: [
            'Daily stiffness without clear cause strongly suggests elevated CRP and IL-6 \u2014 key aging biomarkers.',
            'Frequent stiffness points to low-grade chronic inflammation building in your system.',
            'Activity-related stiffness is normal. Your baseline inflammation appears manageable.',
            'Minimal joint issues suggest your inflammatory markers are likely in a healthy range.'
          ]
        },
        {
          id: 'illness_frequency', text: 'How often do you get sick (colds, flu, infections)?',
          options: [
            { label: 'Monthly', desc: 'I catch everything going around', value: 0 },
            { label: 'Every few months', desc: "More than I'd like", value: 1 },
            { label: 'Once or twice a year', desc: 'Pretty resilient', value: 2 },
            { label: 'Almost never', desc: "Can't remember my last cold", value: 3 }
          ],
          insights: [
            'Frequent illness signals immune dysregulation \u2014 your body is spending resources fighting instead of repairing.',
            'Getting sick every few months suggests your immune system needs reinforcement.',
            'Once or twice a year is solid. Your immune function is performing within healthy parameters.',
            'Exceptional immune resilience. This correlates strongly with lower biological age.'
          ]
        },
        {
          id: 'wound_healing', text: 'How quickly do minor cuts, bruises, or skin irritations heal on your body?',
          options: [
            { label: 'Very slowly', desc: 'Weeks for minor things to clear', value: 0 },
            { label: 'Slower than expected', desc: 'Takes longer than it used to', value: 1 },
            { label: 'Normal pace', desc: 'A week or so for minor cuts', value: 2 },
            { label: 'Very quickly', desc: 'Heals fast, rarely leaves marks', value: 3 }
          ],
          insights: [
            'Slow tissue repair is a direct reflection of impaired cellular regeneration \u2014 elevated senescent cells and suppressed growth factor activity are the likely drivers.',
            'Delayed healing often precedes visible aging signs by years. Your repair cascade is running below optimal capacity.',
            'Normal healing pace is a reasonable baseline. Collagen synthesis and immune-mediated repair are functioning adequately.',
            'Fast healing reflects robust cellular repair machinery \u2014 low senescent cell burden and efficient growth factor signaling. A strong longevity marker.'
          ]
        }
      ]
    },
    {
      id: 'stress', label: 'Stress & Resilience', emoji: '\uD83E\uDDD8',
      description: 'How your nervous system handles and recovers from pressure',
      questions: [
        {
          id: 'stress_recovery', text: 'How would you describe your stress recovery?',
          options: [
            { label: 'I carry it for days', desc: 'Stress lingers and compounds', value: 0 },
            { label: 'Takes a while', desc: 'Need deliberate effort to decompress', value: 1 },
            { label: 'Bounce back fairly quickly', desc: "A good night's sleep usually resets me", value: 2 },
            { label: 'Handle it well', desc: "Stress doesn't stick to me", value: 3 }
          ],
          insights: [
            'Lingering stress keeps cortisol elevated for days \u2014 this is the #1 accelerator of biological aging.',
            'Needing deliberate effort to recover means your parasympathetic nervous system needs support.',
            'Good resilience. Sleep-based recovery is effective, but building active stress tools makes it even better.',
            'Strong stress resilience. Your nervous system is well-regulated \u2014 a powerful anti-aging asset.'
          ]
        },
        {
          id: 'emotional_overwhelm', text: 'How often do you feel emotionally overwhelmed?',
          options: [
            { label: 'Daily', desc: "It's a constant undercurrent", value: 0 },
            { label: 'Several times a week', desc: 'Frequently on edge', value: 1 },
            { label: 'Occasionally', desc: 'Only in high-pressure moments', value: 2 },
            { label: 'Rarely', desc: 'I feel emotionally stable', value: 3 }
          ],
          insights: [
            'Chronic overwhelm disrupts heart-brain coherence and accelerates cellular aging at the chromosomal level.',
            'Frequent emotional pressure keeps your fight-or-flight system activated. Your body never fully rests.',
            'Occasional overwhelm under pressure is human. Your baseline emotional regulation looks healthy.',
            'Emotional stability is a strong indicator of well-regulated cortisol and healthy aging.'
          ]
        },
        {
          id: 'sleep_quality', text: 'How would you rate your sleep quality?',
          options: [
            { label: 'Terrible', desc: 'Wake frequently, rarely feel rested', value: 0 },
            { label: 'Inconsistent', desc: 'Some good nights, many bad ones', value: 1 },
            { label: 'Generally good', desc: 'Usually wake feeling okay', value: 2 },
            { label: 'Deep and restorative', desc: 'I sleep well almost every night', value: 3 }
          ],
          insights: [
            'Poor sleep prevents glymphatic clearance \u2014 your brain can\u2019t clear metabolic waste, accelerating cognitive aging.',
            'Inconsistent sleep disrupts circadian rhythm, which impacts every system from immunity to hormone regulation.',
            'Generally good sleep is a solid foundation. Optimizing deep sleep phases could take this even further.',
            'Deep, restorative sleep is perhaps the single most powerful anti-aging behavior. You\u2019re doing this right.'
          ]
        },
        {
          id: 'social_connection', text: 'How would you describe your social connections and sense of belonging?',
          options: [
            { label: 'Isolated or lonely', desc: 'Few meaningful connections', value: 0 },
            { label: 'A few connections', desc: 'Some relationships, but surface-level', value: 1 },
            { label: 'Good social circle', desc: 'Regular contact with people I trust', value: 2 },
            { label: 'Deep, meaningful relationships', desc: 'I feel genuinely connected and supported', value: 3 }
          ],
          insights: [
            'Chronic loneliness activates the same inflammatory pathways as smoking 15 cigarettes a day. A Brigham Young meta-analysis found social isolation increases mortality risk by 26%. This is among the most underrated aging accelerators.',
            'Surface-level connection leaves the deeper nervous system need for co-regulation unmet. Your threat-detection system stays subtly activated without you realizing it.',
            'A reliable social circle provides nervous system co-regulation \u2014 one of the primary mechanisms by which social health translates to biological health.',
            'Deep relational bonds are one of the strongest predictors of longevity in the Blue Zones research. Your nervous system is getting one of its most powerful inputs.'
          ]
        },
        {
          id: 'screen_time_sleep', text: 'How much screen time do you have in the hour before you fall asleep?',
          options: [
            { label: 'Right until I fall asleep', desc: 'Phone or TV in bed always', value: 0 },
            { label: '30\u201360 minutes', desc: 'Usually winding down with screens', value: 1 },
            { label: 'Under 30 minutes', desc: 'Trying to cut back', value: 2 },
            { label: 'I avoid screens before bed', desc: 'Deliberate wind-down routine', value: 3 }
          ],
          insights: [
            'Evening blue light suppresses melatonin by up to 50% and delays your circadian clock by 1\u20133 hours. This shrinks your deep sleep window every night \u2014 when cellular repair and amyloid clearance occur.',
            'Even 30\u201360 minutes of blue light exposure measurably disrupts melatonin onset. The cumulative sleep debt from this compounds across years.',
            'Reducing screen exposure gives your melatonin system meaningful recovery time. Small improvement, real impact.',
            'Avoiding screens before bed is one of the highest-leverage sleep optimization behaviors. Your glymphatic system is likely performing well.'
          ]
        }
      ]
    },
    {
      id: 'energy', label: 'Energy & Vitality', emoji: '\u26A1',
      description: 'Your daily energy patterns and metabolic resilience',
      questions: [
        {
          id: 'energy_pattern', text: 'How would you describe your daily energy pattern?',
          options: [
            { label: 'Exhausted by noon', desc: "Morning is all I've got", value: 0 },
            { label: 'Afternoon crash', desc: 'Hit a wall after lunch', value: 1 },
            { label: 'Steady with some dips', desc: 'Generally consistent', value: 2 },
            { label: 'Strong all day', desc: 'Even energy from wake to sleep', value: 3 }
          ],
          insights: [
            'Early exhaustion often reflects mitochondrial stress \u2014 your cells\u2019 power plants are struggling.',
            'The afternoon crash is one of the most common signs of blood sugar dysregulation and adrenal fatigue.',
            'Steady energy with minor dips is a good baseline. Fine-tuning nutrition timing could eliminate the dips entirely.',
            'All-day energy reflects strong mitochondrial health and metabolic efficiency \u2014 your cells are thriving.'
          ]
        },
        {
          id: 'caffeine_dependence', text: 'How much do you rely on caffeine or stimulants?',
          options: [
            { label: "Can't function without it", desc: 'Need it to start my day', value: 0 },
            { label: '2\u20133 cups daily', desc: 'Regular habit to keep going', value: 1 },
            { label: 'One cup, more for enjoyment', desc: 'Could skip it if needed', value: 2 },
            { label: "Don't need it", desc: 'Naturally energized', value: 3 }
          ],
          insights: [
            'Caffeine dependency masks underlying energy deficits. Your body is borrowing energy it can\u2019t repay.',
            'Regular caffeine use suggests your natural energy systems need support beyond stimulation.',
            'Using caffeine by choice rather than need is a healthy relationship. Your baseline energy is adequate.',
            'Not needing stimulants is a strong signal of healthy adrenal function and natural vitality.'
          ]
        }
      ]
    },
    {
      id: 'lifestyle', label: 'Lifestyle Baseline', emoji: '\uD83C\uDFC3',
      description: 'Your foundational health habits',
      questions: [
        {
          id: 'exercise_frequency', text: 'How many days per week do you exercise?',
          options: [
            { label: '0\u20131 days', desc: 'Minimal physical activity', value: 0 },
            { label: '2\u20133 days', desc: 'Some regular movement', value: 1 },
            { label: '4\u20135 days', desc: 'Consistent routine', value: 2 },
            { label: '6+ days', desc: 'Daily commitment', value: 3 }
          ],
          insights: [
            'Sedentary lifestyle is one of the most well-documented accelerators of biological aging across every system.',
            'Some movement is better than none. Even adding one more day per week has measurable longevity benefits.',
            'Consistent exercise 4\u20135 days is the sweet spot for longevity research. You\u2019re in optimal range.',
            'Daily movement commitment correlates with 8\u201312 years of reduced biological age in large population studies.'
          ]
        },
        {
          id: 'diet_quality', text: 'How would you describe your overall diet?',
          options: [
            { label: 'Mostly processed', desc: 'Convenience over nutrition', value: 0 },
            { label: 'Mixed', desc: 'Some healthy, some not', value: 1 },
            { label: 'Mostly whole foods', desc: 'I prioritize nutrition', value: 2 },
            { label: 'Very intentional', desc: 'Clean, structured, and consistent', value: 3 }
          ],
          insights: [
            'Processed food drives chronic inflammation, insulin resistance, and oxidative stress \u2014 the aging trifecta.',
            'A mixed diet means your body is fighting inflammation some days and recovering others. Consistency is key.',
            'Whole-food nutrition provides the micronutrients your cells need for repair and regeneration.',
            'Intentional, clean nutrition is one of the most powerful levers for reversing biological age.'
          ]
        },
        {
          id: 'hydration', text: 'How well do you stay hydrated throughout the day?',
          options: [
            { label: 'Poorly', desc: 'Often forget to drink water', value: 0 },
            { label: 'Could be better', desc: "Some days are good, many aren't", value: 1 },
            { label: 'Pretty well', desc: 'Consistent effort', value: 2 },
            { label: 'Excellent', desc: "It's a non-negotiable habit", value: 3 }
          ],
          insights: [
            'Even mild dehydration impairs cognition by 10\u201315% and slows every cellular process in your body.',
            'Inconsistent hydration means your body cycles between efficient and impaired \u2014 consistency changes everything.',
            'Good hydration supports detoxification, nutrient delivery, and cognitive performance.',
            'Excellent hydration is a force multiplier for every other health behavior. Simple but powerful.'
          ]
        },
        {
          id: 'alcohol_consumption', text: 'How many alcoholic drinks do you typically have per week?',
          options: [
            { label: '10 or more', desc: 'Almost daily drinking', value: 0 },
            { label: '5 to 9', desc: 'Several times a week', value: 1 },
            { label: '1 to 4', desc: 'Occasional drinks', value: 2 },
            { label: 'Rarely or never', desc: 'Not part of my routine', value: 3 }
          ],
          insights: [
            'At this level, alcohol accelerates epigenetic aging by an estimated 3\u20136 years. A 2022 meta-analysis in The Lancet found heavy drinking doubles the rate of chromosomal aging.',
            'At 5\u20139 drinks weekly, alcohol disrupts deep sleep architecture, elevates cortisol, and impairs liver detoxification \u2014 a compound aging effect most people underestimate.',
            'Moderate consumption has minimal aging impact if sleep quality is maintained. The key is whether it disrupts your recovery cycles.',
            'Eliminating or minimizing alcohol removes a significant source of oxidative stress and sleep disruption. This single habit has one of the highest ROIs for biological age.'
          ]
        },
        {
          id: 'nature_time', text: 'How much time do you spend outdoors in natural settings each week?',
          options: [
            { label: 'Almost none', desc: 'Mostly indoors, urban environments', value: 0 },
            { label: '1\u20132 hours', desc: 'Some outdoor time, not consistent', value: 1 },
            { label: '3\u20135 hours', desc: 'Regular outdoor exposure', value: 2 },
            { label: '5+ hours', desc: 'Outdoors is a regular part of my routine', value: 3 }
          ],
          insights: [
            'Chronic indoor living is associated with vitamin D deficiency, dysregulated cortisol rhythms, and reduced HRV. A 2019 study in Scientific Reports found under 2 hours weekly in nature significantly elevates inflammatory markers.',
            'Even 1\u20132 hours weekly provides some benefit \u2014 but below 2 hours is where measurable health impacts begin. Consistency matters more than duration.',
            'Regular nature exposure measurably lowers cortisol, improves HRV, and supports circadian regulation. Three to five hours is within the documented sweet spot.',
            'Five-plus hours in nature weekly is associated with 20% lower stress hormones and measurable immune improvements via NK cell activity. This is a legitimate longevity behavior.'
          ]
        }
      ]
    }
  ];

  var MAX_SCORES = { cognitive: 9, inflammation: 12, stress: 15, energy: 6, lifestyle: 15 };

  var RECOMMENDATIONS = {
    cognitive: {
      emoji: '\uD83E\uDDE0',
      title: 'Cognitive Aging Markers Are Elevated',
      body: 'Your responses point to reduced acetylcholine availability, potential dopamine deficit, and suboptimal mitochondrial energy in the brain. Citicoline and Alpha-GPC directly support acetylcholine production, while CoQ10 and NADH fuel cellular energy at the mitochondrial level. Key biomarkers to discuss with your doctor: homocysteine, BDNF serum levels, hs-CRP, B12.',
      cta: 'Explore IgniCognition \u2192',
      ctaUrl: 'https://www.igniton.com/collections/products',
      product: 'IgniCognition\u2122 \u2014 Citicoline, Alpha-GPC, Phosphatidylserine, CoQ10, NADH',
      yearImpact: 0.5,
      freeContent: '<p>Your brain is a use-it-or-lose-it organ. Cognitive challenge is one of the two proven ways to boost BDNF (the other is exercise).</p>'
        + '<a href="/pages/dashboard" class="ba-toolkit__link">Take the Free Memory Challenge \u2192</a>'
        + '<p class="ba-toolkit__note">6 science-backed exercises measuring processing speed, working memory, pattern recognition, and recall. Track your scores over time.</p>'
        + '<a href="/pages/brain-training" class="ba-toolkit__link">Explore Brain Training Tests \u2192</a>'
    },
    inflammation: {
      emoji: '\uD83D\uDD25',
      title: 'Chronic Inflammation Is Your Primary Accelerator',
      body: 'Your pattern suggests elevated IL-6, CRP, and oxidative stress \u2014 primary drivers of accelerated biological aging. Reduced Glutathione is your body\u2019s master antioxidant for detoxification, while Berberine activates AMPK to support metabolic resilience. Resveratrol activates sirtuins for cellular stress response. Ask your doctor about an hs-CRP panel and erythrocyte sedimentation rate.',
      cta: 'Explore IgniLongevity \u2192',
      ctaUrl: 'https://www.igniton.com/collections/products',
      product: 'IgniLongevity\u2122 \u2014 Nicotinamide Riboside, Resveratrol, Glutathione, Berberine, PQQ, Cycloastragenol',
      yearImpact: 1.2,
      freeContent: '<p class="ba-toolkit__subtitle">Add daily (top 10 anti-inflammatory foods):</p>'
        + '<ol class="ba-toolkit__list">'
        + '<li><strong>Wild salmon</strong> \u2014 EPA/DHA omega-3s directly lower IL-6 and CRP</li>'
        + '<li><strong>Blueberries</strong> \u2014 anthocyanins reduce oxidative stress by up to 20%</li>'
        + '<li><strong>Turmeric</strong> (with black pepper) \u2014 curcumin inhibits NF-\u03BAB, the master inflammation switch</li>'
        + '<li><strong>Extra virgin olive oil</strong> \u2014 oleocanthal mimics ibuprofen\u2019s anti-inflammatory action</li>'
        + '<li><strong>Leafy greens</strong> (spinach, kale) \u2014 nitrates improve vascular function</li>'
        + '<li><strong>Walnuts</strong> \u2014 ALA omega-3s + polyphenols for dual anti-inflammatory action</li>'
        + '<li><strong>Ginger</strong> \u2014 6-gingerol inhibits COX-2 and TNF-\u03B1 pathways</li>'
        + '<li><strong>Green tea</strong> \u2014 EGCG activates Nrf2 antioxidant defense pathway</li>'
        + '<li><strong>Broccoli sprouts</strong> \u2014 sulforaphane is one of the strongest Nrf2 activators known</li>'
        + '<li><strong>Fermented foods</strong> (kimchi, sauerkraut) \u2014 gut microbiome diversity directly regulates systemic inflammation</li>'
        + '</ol>'
        + '<p class="ba-toolkit__subtitle">Reduce or eliminate (top 5 inflammatory triggers):</p>'
        + '<ol class="ba-toolkit__list ba-toolkit__list--avoid">'
        + '<li><strong>Seed oils</strong> (soybean, corn, canola) \u2014 excess omega-6 drives arachidonic acid pathway</li>'
        + '<li><strong>Refined sugar</strong> \u2014 spikes insulin and activates AGE (advanced glycation end-product) formation</li>'
        + '<li><strong>Processed meats</strong> \u2014 nitrosamines and AGEs trigger persistent immune activation</li>'
        + '<li><strong>Alcohol</strong> \u2014 acetaldehyde is a direct inflammatory agent and glutathione depleter</li>'
        + '<li><strong>Trans fats</strong> (hydrogenated oils) \u2014 directly damage endothelial cells and raise CRP</li>'
        + '</ol>'
    },
    stress: {
      emoji: '\uD83E\uDDD8',
      title: 'HPA Axis Dysregulation Is Aging You Faster',
      body: 'Your stress and sleep patterns indicate a dysregulated HPA (hypothalamic-pituitary-adrenal) axis \u2014 chronically elevated cortisol is suppressing DHEA-S, disrupting circadian cortisol rhythm, and accelerating chromosomal aging. Key biomarkers: salivary cortisol 4-point test, DHEA-S, and resting HRV.',
      cta: 'Explore IgniLongevity \u2192',
      ctaUrl: 'https://www.igniton.com/collections/products',
      product: 'IgniLongevity\u2122 \u2014 Resveratrol activates sirtuins, Reduced Glutathione supports detox under stress, PQQ for mitochondrial resilience',
      yearImpact: 1.3,
      freeContent: '<p>The 4-7-8 breathing technique activates your vagus nerve and shifts your nervous system from fight-or-flight to rest-and-repair in under 2 minutes.</p>'
        + '<div class="ba-breathe">'
        + '<div class="ba-breathe__circle">'
        + '<span class="ba-breathe__text">Tap to start</span>'
        + '</div>'
        + '<button type="button" class="ba-btn ba-btn--ghost ba-breathe__toggle">Start Breathing Exercise</button>'
        + '</div>'
        + '<p class="ba-toolkit__note">4 seconds inhale \u2022 7 seconds hold \u2022 8 seconds exhale. Complete 4 cycles for one session. Practice twice daily for measurable HRV improvement within 2 weeks.</p>'
    },
    energy: {
      emoji: '\u26A1',
      title: 'Mitochondrial Decline Is Driving Your Energy Pattern',
      body: 'Energy crashes and stimulant dependence indicate mitochondrial stress and likely reduced NAD+ availability. NAD+ declines ~50% between ages 40\u201360. Nicotinamide Riboside directly boosts NAD+ production, PQQ promotes new mitochondria creation, and CoQ10/NADH fuel ATP at the cellular level. Key biomarkers: serum NAD+, thyroid panel (TSH, free T3), hemoglobin A1c.',
      cta: 'Explore IgniCognition + IgniLongevity Bundle \u2192',
      ctaUrl: 'https://www.igniton.com/collections/products',
      product: 'IgniCognition\u2122 (CoQ10 + NADH) + IgniLongevity\u2122 (NR + PQQ) Bundle',
      yearImpact: 0.8,
      freeContent: '<p>Movement is the fastest proven way to boost mitochondrial biogenesis and NAD+ production. Start small, build momentum.</p>'
        + '<div class="ba-toolkit__days">'
        + '<div class="ba-toolkit__day"><strong>Day 1\u20132:</strong> 10-minute brisk morning walk (before coffee)</div>'
        + '<div class="ba-toolkit__day"><strong>Day 3\u20134:</strong> 15-minute walk + 5 bodyweight squats every hour</div>'
        + '<div class="ba-toolkit__day"><strong>Day 5:</strong> 20-minute zone 2 cardio (conversational pace)</div>'
        + '<div class="ba-toolkit__day"><strong>Day 6:</strong> 10-minute walk + 3 sets of 10 push-ups or wall push-ups</div>'
        + '<div class="ba-toolkit__day"><strong>Day 7:</strong> 30-minute sustained activity of your choice</div>'
        + '</div>'
        + '<p class="ba-toolkit__note">Zone 2 cardio (where you can still hold a conversation) specifically stimulates mitochondrial biogenesis. Even this minimal protocol creates measurable changes in energy markers within 7 days.</p>'
    },
    lifestyle: {
      emoji: '\uD83C\uDFC3',
      title: 'Your Foundation Is Creating Compound Aging',
      body: 'Exercise, diet, alcohol, and nature exposure are your highest-leverage levers \u2014 they govern oxidative stress, insulin sensitivity, and inflammatory baseline simultaneously. The compound effect of improving multiple lifestyle factors has more documented aging-reversal evidence than any single supplement.',
      cta: 'Explore Our Solutions \u2192',
      ctaUrl: 'https://www.igniton.com/collections/products',
      product: null,
      yearImpact: 1.5,
      freeContent: '<p>Your circadian clock governs hormone production, DNA repair timing, and metabolic efficiency. Resetting it is the single highest-ROI lifestyle change for biological age.</p>'
        + '<div class="ba-toolkit__days">'
        + '<div class="ba-toolkit__day"><strong>Step 1 \u2014 Morning light:</strong> Get 10+ minutes of sunlight within 30 minutes of waking. No sunglasses. This sets your cortisol peak and melatonin timer.</div>'
        + '<div class="ba-toolkit__day"><strong>Step 2 \u2014 Meal timing:</strong> Eat within a 10-hour window (e.g., 8am\u20136pm). No food within 3 hours of bedtime. This aligns peripheral clocks in your liver and gut.</div>'
        + '<div class="ba-toolkit__day"><strong>Step 3 \u2014 Screen curfew:</strong> No screens 60 minutes before bed. If unavoidable, use night mode + blue-light glasses. This protects your melatonin onset.</div>'
        + '<div class="ba-toolkit__day"><strong>Step 4 \u2014 Temperature:</strong> Keep your bedroom at 65\u201368\u00B0F (18\u201320\u00B0C). Core body temperature drop is a critical sleep-onset signal.</div>'
        + '<div class="ba-toolkit__day"><strong>Step 5 \u2014 Consistent wake time:</strong> Same wake time every day (\u00B130 min), including weekends. This is the single most important circadian anchor.</div>'
        + '</div>'
        + '<p class="ba-toolkit__note">Most people notice improved energy, mood, and sleep quality within 3\u20135 days of following this protocol consistently. Track your subjective energy on a 1\u201310 scale each morning.</p>'
    }
  };

  var TIER_COLORS = { optimal: '#2ECC71', ontrack: '#D3A23C', accel: '#E67E22', concern: '#E74C3C' };
  var TIER_LABELS = { optimal: 'Optimal', ontrack: 'On Track', accel: 'Accelerated', concern: 'Concern' };
  var TIER_MSGS = {
    optimal: "Your biology is outpacing your birthday. You\u2019re doing something right \u2014 keep optimizing.",
    ontrack: "You\u2019re aging at a normal pace \u2014 but normal isn\u2019t optimal. Small changes can shift this significantly.",
    accel: 'Your body is showing signs of accelerated aging. The good news: the markers driving this score are modifiable.',
    concern: 'Multiple indicators suggest your biology is aging faster than it should. But these patterns are reversible with targeted interventions.'
  };

  var ACCELERATOR_LABELS = {
    brain_fog: { label: 'Chronic brain fog' },
    memory: { label: 'Memory impairment' },
    focus_switching: { label: 'Poor task switching' },
    recovery_time: { label: 'Slow physical recovery' },
    joint_stiffness: { label: 'Chronic joint stiffness' },
    illness_frequency: { label: 'Frequent illness' },
    wound_healing: { label: 'Impaired tissue repair' },
    stress_recovery: { label: 'Poor stress recovery' },
    emotional_overwhelm: { label: 'Emotional overwhelm' },
    sleep_quality: { label: 'Poor sleep quality' },
    social_connection: { label: 'Social isolation' },
    screen_time_sleep: { label: 'Evening screen exposure' },
    energy_pattern: { label: 'Energy crashes' },
    caffeine_dependence: { label: 'Stimulant dependence' },
    exercise_frequency: { label: 'Insufficient exercise' },
    diet_quality: { label: 'Poor diet quality' },
    hydration: { label: 'Chronic dehydration' },
    alcohol_consumption: { label: 'Alcohol consumption' },
    nature_time: { label: 'Insufficient nature exposure' }
  };

  var BIOMARKER_PROFILES = {
    inflammation: {
      low: ['Elevated CRP (C-Reactive Protein)', 'Elevated IL-6 (Interleukin-6)', 'Impaired leukocyte function'],
      mid: ['Mildly elevated CRP', 'Suboptimal IL-6 regulation'],
      high: ['CRP likely within healthy range', 'IL-6 regulation appears adequate']
    },
    cognitive: {
      low: ['Reduced BDNF (brain-derived neurotrophic factor)', 'Potential acetylcholine deficit', 'Elevated neuroinflammatory markers'],
      mid: ['Mildly reduced BDNF activity', 'Suboptimal dopamine receptor sensitivity'],
      high: ['BDNF levels appear adequate', 'Acetylcholine synthesis likely normal']
    },
    stress: {
      low: ['Dysregulated cortisol rhythm (HPA axis)', 'Reduced HRV (Heart Rate Variability)', 'Suppressed DHEA-S production'],
      mid: ['Mild cortisol elevation likely', 'HRV may be below optimal range'],
      high: ['Cortisol rhythm appears regulated', 'HRV likely in healthy range']
    },
    energy: {
      low: ['Mitochondrial dysfunction indicators', 'Likely reduced NAD+ availability', 'Potential thyroid sub-optimality'],
      mid: ['Mildly reduced mitochondrial efficiency', 'Borderline NAD+ levels'],
      high: ['Mitochondrial function appears adequate', 'NAD+ production likely normal']
    },
    lifestyle: {
      low: ['Elevated oxidative stress markers', 'Potential insulin sensitivity decline', 'Likely vitamin D deficiency'],
      mid: ['Mild oxidative stress burden', 'Moderate insulin sensitivity'],
      high: ['Oxidative stress burden appears low', 'Insulin sensitivity likely healthy']
    }
  };

  // --- Biomarker Evidence (for "Why?" explainability) ---
  var BIOMARKER_EVIDENCE = {
    'Elevated CRP (C-Reactive Protein)': function (a) {
      var reasons = [];
      if (a.joint_stiffness <= 1) reasons.push('chronic joint stiffness');
      if (a.wound_healing <= 1) reasons.push('slow wound healing');
      if (a.illness_frequency <= 1) reasons.push('frequent illness');
      if (a.diet_quality <= 1) reasons.push('a processed-food diet that feeds inflammatory pathways');
      return reasons.length ? 'Your reported ' + reasons.join(' and ') + ' suggest elevated systemic inflammation. CRP is the primary blood marker your doctor would check to confirm this \u2014 ask for an hs-CRP panel.' : null;
    },
    'Elevated IL-6 (Interleukin-6)': function (a) {
      var reasons = [];
      if (a.recovery_time <= 1) reasons.push('slow exercise recovery');
      if (a.wound_healing <= 1) reasons.push('delayed tissue repair');
      if (a.stress_recovery <= 1) reasons.push('poor stress recovery (stress elevates IL-6 directly)');
      return reasons.length ? 'Your ' + reasons.join(', ') + ' are consistent with elevated IL-6. This cytokine drives accelerated aging through chronic low-grade inflammation.' : null;
    },
    'Impaired leukocyte function': function (a) {
      if (a.illness_frequency <= 1 && a.stress_recovery <= 1) return 'Frequent illness combined with poor stress recovery suggests immune cell function is compromised. Chronic cortisol suppresses white blood cell responsiveness.';
      if (a.illness_frequency <= 1) return 'Getting sick frequently indicates your immune cells may not be responding effectively to pathogens \u2014 often linked to chronic inflammation or nutrient depletion.';
      return null;
    },
    'Reduced BDNF (brain-derived neurotrophic factor)': function (a) {
      var reasons = [];
      if (a.brain_fog <= 1) reasons.push('frequent brain fog');
      if (a.memory <= 1) reasons.push('memory difficulties');
      if (a.exercise_frequency <= 1) reasons.push('insufficient exercise (exercise is the #1 BDNF booster)');
      return reasons.length ? 'Your ' + reasons.join(' and ') + ' suggest reduced BDNF \u2014 the protein that supports new neural connections. Exercise and cognitive challenge are the two most effective ways to boost it.' : null;
    },
    'Potential acetylcholine deficit': function (a) {
      if (a.focus_switching <= 1 && a.memory <= 1) return 'Difficulty with task-switching and memory together point to reduced acetylcholine \u2014 the neurotransmitter essential for focus and memory consolidation. Citicoline and Alpha-GPC directly support its production.';
      if (a.focus_switching <= 1) return 'Your difficulty switching between tasks efficiently is a hallmark of suboptimal acetylcholine activity. This neurotransmitter governs the speed of cognitive transitions.';
      return null;
    },
    'Elevated neuroinflammatory markers': function (a) {
      if (a.brain_fog <= 1 && a.sleep_quality <= 1) return 'Daily brain fog combined with poor sleep is strongly associated with neuroinflammation. During deep sleep, your brain\u2019s glymphatic system clears inflammatory waste \u2014 poor sleep leaves that waste accumulating.';
      return null;
    },
    'Dysregulated cortisol rhythm (HPA axis)': function (a) {
      var reasons = [];
      if (a.stress_recovery <= 1) reasons.push('slow stress recovery');
      if (a.sleep_quality <= 1) reasons.push('poor sleep quality');
      if (a.emotional_overwhelm <= 1) reasons.push('emotional overwhelm');
      return reasons.length ? 'Your ' + reasons.join(', ') + ' are textbook signs of HPA axis dysregulation. A 4-point salivary cortisol test would confirm whether your cortisol rhythm is flat, inverted, or simply elevated.' : null;
    },
    'Reduced HRV (Heart Rate Variability)': function (a) {
      if (a.stress_recovery <= 1 && a.exercise_frequency <= 1) return 'Poor stress recovery and limited exercise are the two strongest predictors of low HRV. Heart rate variability reflects your nervous system\u2019s flexibility \u2014 higher is biologically younger.';
      if (a.stress_recovery <= 1) return 'Your stress recovery pattern suggests reduced HRV. This metric is increasingly recognized as one of the best single biomarkers of biological age.';
      return null;
    },
    'Suppressed DHEA-S production': function (a) {
      if (a.stress_recovery <= 1 && a.energy_pattern <= 1) return 'Chronic stress suppresses DHEA-S while elevating cortisol \u2014 your energy crashes and stress pattern together suggest this cortisol-to-DHEA ratio may be skewed.';
      return null;
    },
    'Mitochondrial dysfunction indicators': function (a) {
      if (a.energy_pattern <= 1 && a.caffeine_dependence <= 1) return 'Energy crashes combined with stimulant dependence suggest your mitochondria \u2014 the power plants of every cell \u2014 are struggling to meet ATP demand. Caffeine masks this but doesn\u2019t fix it.';
      return null;
    },
    'Likely reduced NAD+ availability': function (a) {
      if (a.energy_pattern <= 1) return 'NAD+ naturally declines ~50% between ages 40\u201360, and your energy pattern is consistent with this decline. NAD+ is required for mitochondrial energy production, DNA repair, and sirtuin activation.';
      return null;
    },
    'Potential thyroid sub-optimality': function (a) {
      if (a.energy_pattern <= 1 && a.brain_fog <= 1) return 'The combination of energy crashes and brain fog can indicate subclinical thyroid issues. Ask your doctor for a full panel: TSH, free T3, free T4, and thyroid antibodies \u2014 not just TSH alone.';
      return null;
    },
    'Elevated oxidative stress markers': function (a) {
      if (a.diet_quality <= 1 && a.exercise_frequency <= 1) return 'A processed diet without regular exercise creates a double hit: more free radical production from poor fuel, less antioxidant enzyme activation from missing movement. This accelerates cellular aging.';
      if (a.alcohol_consumption <= 1) return 'Regular alcohol consumption generates acetaldehyde \u2014 a potent source of oxidative stress that damages DNA and depletes glutathione, your body\u2019s master antioxidant.';
      return null;
    },
    'Potential insulin sensitivity decline': function (a) {
      if (a.diet_quality <= 1 && a.energy_pattern <= 1) return 'Energy crashes combined with poor diet quality suggest blood glucose instability \u2014 your cells may be becoming less responsive to insulin, a precursor to metabolic aging.';
      return null;
    },
    'Likely vitamin D deficiency': function (a) {
      if (a.nature_time <= 1) return 'Your limited outdoor exposure puts you at high risk for vitamin D deficiency \u2014 affecting over 40% of adults. Vitamin D regulates over 200 genes related to immune function and inflammation.';
      return null;
    },
    'Mildly elevated CRP': function () { return null; },
    'Suboptimal IL-6 regulation': function () { return null; },
    'CRP likely within healthy range': function () { return null; },
    'IL-6 regulation appears adequate': function () { return null; },
    'Mildly reduced BDNF activity': function (a) {
      if (a.exercise_frequency <= 1) return 'Your exercise frequency is the most likely factor. BDNF production is strongly exercise-dependent \u2014 even moderate increases in activity measurably boost levels.';
      return null;
    },
    'Suboptimal dopamine receptor sensitivity': function () { return null; },
    'BDNF levels appear adequate': function () { return null; },
    'Acetylcholine synthesis likely normal': function () { return null; },
    'Mild cortisol elevation likely': function (a) {
      if (a.sleep_quality <= 1) return 'Your sleep quality may be keeping cortisol slightly elevated. The cortisol-sleep relationship is bidirectional \u2014 poor sleep raises cortisol, which then makes sleep harder.';
      return null;
    },
    'HRV may be below optimal range': function () { return null; },
    'Cortisol rhythm appears regulated': function () { return null; },
    'HRV likely in healthy range': function () { return null; },
    'Mildly reduced mitochondrial efficiency': function () { return null; },
    'Borderline NAD+ levels': function () { return null; },
    'Mitochondrial function appears adequate': function () { return null; },
    'NAD+ production likely normal': function () { return null; },
    'Mild oxidative stress burden': function () { return null; },
    'Moderate insulin sensitivity': function () { return null; },
    'Oxidative stress burden appears low': function () { return null; },
    'Insulin sensitivity likely healthy': function () { return null; }
  };

  // --- Cross-Referenced Insights ---
  var CROSS_REFS = {
    caffeine_dependence: function (oi, a) {
      if (oi <= 1 && a.sleep_quality !== undefined && a.sleep_quality <= 1 && a.stress_recovery !== undefined && a.stress_recovery <= 1) {
        return 'Given your sleep quality and stress recovery patterns, this caffeine dependence is likely masking a deeper cortisol dysregulation cycle. Your body is borrowing energy it cannot repay \u2014 and each cup raises cortisol by 15\u201330%, making the underlying pattern worse.';
      }
      if (oi <= 1 && a.sleep_quality !== undefined && a.sleep_quality <= 1) {
        return 'Your poor sleep and caffeine dependence are likely in a reinforcing loop: poor sleep drives caffeine need, caffeine suppresses adenosine receptors, which degrades sleep quality the following night. This cycle is a well-documented accelerator of adrenal fatigue.';
      }
      if (oi <= 1 && a.energy_pattern !== undefined && a.energy_pattern <= 1) {
        return 'Your energy pattern and caffeine reliance together suggest your mitochondria are struggling rather than your willpower. Stimulants address the symptom but not the source \u2014 your cells\u2019 ATP production efficiency needs support.';
      }
      return null;
    },
    energy_pattern: function (oi, a) {
      if (oi <= 1 && a.sleep_quality !== undefined && a.sleep_quality <= 1) {
        return 'Your energy crash and sleep quality together paint a clear picture: your body isn\u2019t completing the overnight cellular repair cycles that restore mitochondrial function. What feels like an energy problem is actually a recovery deficit.';
      }
      if (oi <= 1 && a.diet_quality !== undefined && a.diet_quality <= 1) {
        return 'Low energy combined with a processed-food diet strongly suggests blood glucose instability as the driver. Your cells are getting fuel, but not the kind they can use efficiently \u2014 insulin spikes followed by crashes.';
      }
      return null;
    },
    wound_healing: function (oi, a) {
      if (oi <= 1 && a.recovery_time !== undefined && a.recovery_time <= 1 && a.diet_quality !== undefined && a.diet_quality <= 1) {
        return 'Your slow exercise recovery and delayed tissue healing, combined with your diet, form a consistent picture of systemic inflammatory burden. Elevated IL-6 and reduced IGF-1 signaling are the likely underlying mechanisms.';
      }
      if (oi <= 1 && a.recovery_time !== undefined && a.recovery_time <= 1) {
        return 'Both your exercise recovery and wound healing are running slow \u2014 this consistent pattern across two different repair systems points to systemic rather than localized inflammation. Your regenerative resources are stretched thin.';
      }
      return null;
    },
    screen_time_sleep: function (oi, a) {
      if (oi <= 1 && a.sleep_quality !== undefined && a.sleep_quality <= 1) {
        return 'Your screen habits may be a significant driver of the sleep quality you reported earlier. Evening blue light blocks melatonin production for 90\u2013180 minutes after exposure \u2014 meaning the window for deep, restorative sleep is being cut shorter than most people realize. This is one of the most modifiable factors in your profile.';
      }
      if (oi <= 1 && a.stress_recovery !== undefined && a.stress_recovery <= 1) {
        return 'With your stress recovery pattern and evening screen use, your nervous system may never be reaching parasympathetic dominance before sleep. Without that wind-down phase, your cortisol rhythm stays disrupted.';
      }
      return null;
    },
    social_connection: function (oi, a) {
      if (oi <= 1 && a.emotional_overwhelm !== undefined && a.emotional_overwhelm <= 1 && a.stress_recovery !== undefined && a.stress_recovery <= 1) {
        return 'Your social isolation, emotional overwhelm, and slow stress recovery are interconnected at a neurological level. Without co-regulation through relationships, your nervous system manages all threat-assessment alone \u2014 keeping your sympathetic system chronically activated. This triad is one of the strongest aging accelerators in the behavioral literature.';
      }
      if (oi <= 1 && a.emotional_overwhelm !== undefined && a.emotional_overwhelm <= 1) {
        return 'The emotional overwhelm you described and your social connection level are likely linked. Humans are wired for relational regulation \u2014 the nervous system genuinely cannot down-regulate stress as efficiently without trusted others. This isn\u2019t a soft finding; it\u2019s measurable in cortisol and HRV data.';
      }
      return null;
    },
    alcohol_consumption: function (oi, a) {
      if (oi <= 1 && a.sleep_quality !== undefined && a.sleep_quality <= 1) {
        return 'Alcohol is almost certainly contributing to the sleep issues you described. While it accelerates sleep onset, it suppresses REM sleep in the second half of the night \u2014 when emotional memory consolidation and metabolic recovery occur. The sleep you\u2019re getting may feel like rest but isn\u2019t providing full repair.';
      }
      if (oi <= 1 && a.recovery_time !== undefined && a.recovery_time <= 1) {
        return 'Your slow exercise recovery and regular alcohol use are likely compounding each other. Alcohol impairs protein synthesis by up to 37%, directly slowing muscle repair and elevating systemic inflammation.';
      }
      return null;
    },
    joint_stiffness: function (oi, a) {
      if (oi <= 1 && a.diet_quality !== undefined && a.diet_quality <= 1) {
        return 'Your joint stiffness combined with a processed-food diet is a textbook profile for arachidonic acid-driven inflammation. Processed oils and refined carbs directly feed the COX-2 inflammatory pathway \u2014 what shows up as joint pain is your immune system responding to constant dietary provocation.';
      }
      return null;
    },
    illness_frequency: function (oi, a) {
      if (oi <= 1 && a.stress_recovery !== undefined && a.stress_recovery <= 1) {
        return 'Chronic stress suppresses natural killer cell activity and IgA secretion \u2014 your two primary frontline immune defenses. Given your stress recovery pattern, your immune vulnerability is likely stress-mediated rather than a nutrient deficiency alone.';
      }
      return null;
    },
    nature_time: function (oi, a) {
      if (oi <= 1 && a.stress_recovery !== undefined && a.stress_recovery <= 1) {
        return 'Given your stress recovery, outdoor exposure could be a particularly high-leverage intervention for you. Shinrin-yoku (forest bathing) research shows 20-minute sessions lower salivary cortisol by 13\u201316% and increase HRV \u2014 your nervous system responds to natural environments at a physiological level.';
      }
      return null;
    }
  };

  function getInsight(q, optionIndex) {
    var a = state.answers;
    var cross = CROSS_REFS[q.id];
    if (cross) {
      var result = cross(optionIndex, a);
      if (result) return { text: result, isCross: true };
    }
    return { text: q.insights[optionIndex], isCross: false };
  }

  // --- Personalized Narrative Engine ---
  var NARRATIVE_SIGNALS = [
    // Sleep + caffeine loop
    { test: function (a) { return a.sleep_quality <= 1 && a.caffeine_dependence <= 1; },
      text: 'Your reliance on caffeine alongside disrupted sleep suggests your brain is compensating rather than thriving \u2014 a cortisol-borrowing cycle that compounds over time.' },
    // Stress + social isolation
    { test: function (a) { return a.social_connection <= 1 && a.stress_recovery <= 1; },
      text: 'Limited social connection combined with slow stress recovery points to a nervous system stuck in vigilance mode \u2014 without co-regulation, your body bears the full burden of downregulation alone.' },
    // Stress + emotional overwhelm
    { test: function (a) { return a.emotional_overwhelm <= 1 && a.stress_recovery <= 1 && (a.social_connection === undefined || a.social_connection > 1); },
      text: 'Your stress recovery and emotional overwhelm patterns suggest a chronically activated sympathetic nervous system \u2014 your HPA axis is running hot, which accelerates aging across every system.' },
    // Inflammation + diet
    { test: function (a) { return a.joint_stiffness <= 1 && a.diet_quality <= 1; },
      text: 'The joint stiffness you described, coupled with your dietary patterns, suggests your inflammatory baseline is being actively fed by what you eat \u2014 a cycle your body can\u2019t resolve without intervention.' },
    // Inflammation + wound healing
    { test: function (a) { return a.wound_healing <= 1 && a.recovery_time <= 1; },
      text: 'Slow tissue repair and prolonged exercise recovery reveal that your body\u2019s regenerative systems are running at reduced capacity \u2014 consistent with elevated inflammatory load.' },
    // Energy + sleep deficit
    { test: function (a) { return a.energy_pattern <= 1 && a.sleep_quality <= 1; },
      text: 'What feels like an energy problem is actually a recovery deficit \u2014 your body isn\u2019t completing the overnight repair cycles that restore mitochondrial function.' },
    // Alcohol + sleep
    { test: function (a) { return a.alcohol_consumption <= 1 && a.sleep_quality <= 1; },
      text: 'Your alcohol intake is almost certainly degrading the sleep quality you reported \u2014 suppressing REM sleep and preventing full metabolic repair, even when you feel like you slept enough.' },
    // Screen + circadian
    { test: function (a) { return a.screen_time_sleep <= 1 && a.sleep_quality <= 1; },
      text: 'Evening screen exposure is likely disrupting your melatonin production by 90\u2013180 minutes, compressing the window for restorative deep sleep your body depends on.' },
    // Nature deprivation + stress
    { test: function (a) { return a.nature_time <= 1 && a.stress_recovery <= 1; },
      text: 'Your limited nature exposure removes one of the most accessible cortisol-lowering tools available to you \u2014 even 20 minutes outdoors measurably shifts HRV and stress hormones.' },
    // Strong cognitive
    { test: function (a) { return a.brain_fog >= 2 && a.memory >= 2 && a.focus_switching >= 2; },
      text: 'Your cognitive function shows strong baseline capacity \u2014 your brain is performing well relative to your other systems.' },
    // Good exercise
    { test: function (a) { return a.exercise_frequency >= 2; },
      text: 'Your exercise routine is working in your favor \u2014 regular physical activity is one of the strongest documented interventions for biological age reversal.' },
    // Illness + stress-mediated immunity
    { test: function (a) { return a.illness_frequency <= 1 && a.stress_recovery <= 1; },
      text: 'Your frequent illness pattern appears stress-mediated \u2014 chronic cortisol elevation suppresses the NK cells and IgA that form your frontline immune defense.' },
    // Alcohol standalone
    { test: function (a) { return a.alcohol_consumption <= 1 && (a.sleep_quality === undefined || a.sleep_quality > 1); },
      text: 'At your level of alcohol consumption, research shows accelerated epigenetic aging and doubled chromosomal degradation rates \u2014 this is one of your highest-leverage areas for improvement.' },
    // Good hydration + diet
    { test: function (a) { return a.hydration >= 2 && a.diet_quality >= 2; },
      text: 'Your hydration and dietary habits provide a solid metabolic foundation \u2014 your cells are getting the raw materials they need for repair and maintenance.' },
    // Everything poor (concern tier fallback)
    { test: function (a) { return a.sleep_quality <= 1 && a.stress_recovery <= 1 && a.diet_quality <= 1 && a.exercise_frequency <= 1; },
      text: 'Multiple foundational systems are under strain simultaneously \u2014 but this also means targeted changes in even one area will create cascading improvements across the others.' }
  ];

  function generateNarrative(r) {
    var a = state.answers;
    var fragments = [];
    NARRATIVE_SIGNALS.forEach(function (sig) {
      try {
        if (sig.test(a)) fragments.push(sig.text);
      } catch (e) { /* answer undefined — skip */ }
    });
    // Take top 3 most relevant
    fragments = fragments.slice(0, 3);
    if (fragments.length === 0) {
      if (r.tier === 'optimal') return 'Your lifestyle patterns are well-aligned with longevity research. You\u2019re making choices that actively protect your biological age \u2014 the key now is consistency and targeted optimization.';
      return 'Your results reveal a unique combination of strengths and vulnerabilities. The patterns below highlight where your biology is aging faster than expected \u2014 and where you have the most room to shift the trajectory.';
    }
    var closer = ' At ' + r.chronologicalAge + ', your biology is behaving closer to ' + r.biologicalAge + ', primarily driven by ' + (r.weakestCategories[0] === 'stress' ? 'stress and recovery deficits' : r.weakestCategories[0] === 'inflammation' ? 'inflammatory burden' : r.weakestCategories[0] === 'cognitive' ? 'cognitive decline markers' : r.weakestCategories[0] === 'energy' ? 'mitochondrial energy deficits' : 'lifestyle factors') + ' rather than any single behavior.';
    if (r.tier === 'optimal') closer = ' At ' + r.chronologicalAge + ', your biology is performing like ' + r.biologicalAge + ' \u2014 keep building on what\u2019s working.';
    return fragments.join(' ') + closer;
  }

  // Flatten all questions for sequential navigation
  var ALL_QUESTIONS = [];
  CATEGORIES.forEach(function (cat) {
    cat.questions.forEach(function (q) {
      ALL_QUESTIONS.push({ catId: cat.id, catLabel: cat.label, catEmoji: cat.emoji, catDesc: cat.description, q: q });
    });
  });

  // --- State ---
  var state = { qi: 0, answers: {}, age: null, result: null };
  var root;

  // --- Helpers ---
  function qs(sel) { return root.querySelector(sel); }
  function qsa(sel) { return root.querySelectorAll(sel); }

  function showScreen(name) {
    qsa('.ba-screen').forEach(function (el) { el.classList.remove('is-active'); });
    var target = qs('[data-screen="' + name + '"]');
    if (!target) return;
    target.classList.add('is-active');

    // GSAP entrance
    if (window.gsap) {
      gsap.fromTo(target, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' });
    } else {
      target.style.opacity = '1';
    }
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // --- Render Question ---
  function renderQuestion(index, direction) {
    var data = ALL_QUESTIONS[index];
    var cat = data;
    var q = data.q;
    var total = ALL_QUESTIONS.length;

    // Find which category index we're in
    var catIndex = CATEGORIES.findIndex(function (c) { return c.id === cat.catId; });

    // Build progress segments
    var segsHtml = '';
    for (var i = 0; i < CATEGORIES.length; i++) {
      var cls = 'ba-progress__seg';
      if (i < catIndex) cls += ' is-done';
      else if (i === catIndex) cls += ' is-current';
      segsHtml += '<div class="' + cls + '"></div>';
    }

    // Build options
    var optsHtml = '';
    q.options.forEach(function (opt, oi) {
      var sel = state.answers[q.id] === opt.value ? ' is-selected' : '';
      optsHtml += '<button type="button" class="ba-q-option' + sel + '" data-value="' + opt.value + '" data-oi="' + oi + '">'
        + '<span class="ba-q-option__dot"></span>'
        + '<span class="ba-q-option__text">'
        + '<span class="ba-q-option__label">' + opt.label + '</span>'
        + '<span class="ba-q-option__desc">' + opt.desc + '</span>'
        + '</span></button>';
    });

    // Existing insight?
    var insightHtml = '';
    if (state.answers[q.id] !== undefined) {
      var insightIdx = state.answers[q.id];
      var ins = getInsight(q, insightIdx);
      insightHtml = '<div class="ba-insight' + (ins.isCross ? ' ba-insight--cross' : '') + '" style="opacity:1;transform:none;">' + ins.text + '</div>';
    }

    var screenEl = qs('[data-screen="question"]');
    screenEl.innerHTML = '<div class="ba-progress">'
      + '<div class="ba-progress__segments">' + segsHtml + '</div>'
      + '<div class="ba-progress__meta"><span>Question ' + (index + 1) + ' of ' + total + '</span><span>' + cat.catLabel + '</span></div>'
      + '</div>'
      + '<div class="ba-q-category"><span class="ba-q-category__emoji">' + cat.catEmoji + '</span><span class="ba-q-category__label">' + cat.catLabel + '</span></div>'
      + '<p class="ba-q-category__desc">' + cat.catDesc + '</p>'
      + '<h2 class="ba-q-text">' + q.text + '</h2>'
      + '<div class="ba-q-options">' + optsHtml + '</div>'
      + '<div class="ba-insight-slot">' + insightHtml + '</div>'
      + '<div class="ba-q-nav">'
      + '<button type="button" class="ba-btn ba-btn--ghost ba-btn--back">\u2190 Back</button>'
      + '<button type="button" class="ba-btn ba-btn--gold ba-btn--next"' + (state.answers[q.id] !== undefined ? '' : ' disabled') + '>'
      + (index < total - 1 ? 'Continue \u2192' : 'See Results \u2192')
      + '</button></div>';

    showScreen('question');

    // GSAP stagger options entrance
    if (window.gsap) {
      gsap.fromTo(screenEl.querySelectorAll('.ba-q-option'), { opacity: 0, x: direction === 'back' ? -20 : 20 }, { opacity: 1, x: 0, duration: 0.3, stagger: 0.06, ease: 'power2.out' });
    }

    // Bind option clicks
    screenEl.querySelectorAll('.ba-q-option').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var val = parseInt(this.getAttribute('data-value'), 10);
        var oi = parseInt(this.getAttribute('data-oi'), 10);
        state.answers[q.id] = val;

        // Update UI
        screenEl.querySelectorAll('.ba-q-option').forEach(function (b) { b.classList.remove('is-selected'); });
        this.classList.add('is-selected');

        // GSAP pulse
        if (window.gsap) {
          gsap.fromTo(this, { scale: 1 }, { scale: 1.02, duration: 0.12, yoyo: true, repeat: 1, ease: 'power2.out' });
        }

        // Show insight
        var slot = screenEl.querySelector('.ba-insight-slot');
        var ins = getInsight(q, oi);
        slot.innerHTML = '<div class="ba-insight' + (ins.isCross ? ' ba-insight--cross' : '') + '">' + ins.text + '</div>';
        var insightEl = slot.querySelector('.ba-insight');
        if (window.gsap) {
          gsap.fromTo(insightEl, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' });
        } else {
          insightEl.style.opacity = '1';
          insightEl.style.transform = 'none';
        }

        // Enable next
        var nextBtn = screenEl.querySelector('.ba-btn--next');
        if (nextBtn) nextBtn.disabled = false;
      });
    });

    // Nav
    screenEl.querySelector('.ba-btn--back').addEventListener('click', function () {
      if (index > 0) {
        state.qi = index - 1;
        renderQuestion(state.qi, 'back');
      } else {
        showScreen('age');
      }
    });

    screenEl.querySelector('.ba-btn--next').addEventListener('click', function () {
      if (index < total - 1) {
        state.qi = index + 1;
        renderQuestion(state.qi, 'forward');
      } else {
        showCalcScreen();
      }
    });
  }

  // --- Calculating Screen ---
  function showCalcScreen() {
    showScreen('calculating');
    var steps = qsa('.ba-calc__step');
    var delay = 0;

    steps.forEach(function (step, i) {
      setTimeout(function () {
        if (window.gsap) {
          gsap.to(step, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' });
        } else {
          step.style.opacity = '1';
          step.style.transform = 'none';
        }

        setTimeout(function () {
          step.classList.add('is-done');
          step.querySelector('.ba-calc__icon').innerHTML = '<svg class="ba-calc__check" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>';

          if (i === steps.length - 1) {
            setTimeout(showResults, 600);
          }
        }, 400);
      }, delay);
      delay += 650;
    });
  }

  // --- Scoring ---
  function calculateResult() {
    var catScores = {};
    var total = 0;
    CATEGORIES.forEach(function (cat) {
      var s = 0;
      cat.questions.forEach(function (q) { s += (state.answers[q.id] || 0); });
      catScores[cat.id] = { score: s, max: MAX_SCORES[cat.id], percent: Math.round((s / MAX_SCORES[cat.id]) * 100) };
      total += s;
    });

    var age = state.age;
    var offset;
    // Thresholds scaled for 57-point max (was 42)
    if (total >= 49) offset = -Math.round(3 + ((total - 49) / 8) * 4);
    else if (total >= 35) offset = Math.round(2 - ((total - 35) / 14) * 4);
    else if (total >= 22) offset = Math.round(3 + ((34 - total) / 13) * 5);
    else offset = Math.round(8 + ((21 - total) / 22) * 7);

    var tier;
    if (offset <= -3) tier = 'optimal';
    else if (offset <= 2) tier = 'ontrack';
    else if (offset <= 8) tier = 'accel';
    else tier = 'concern';

    var weakest = Object.entries(catScores).sort(function (a, b) { return a[1].percent - b[1].percent; }).slice(0, 2).map(function (e) { return e[0]; });

    // Compute aging accelerators (questions scored 0 or 1)
    var acceleratorItems = [];
    ALL_QUESTIONS.forEach(function (item) {
      var qId = item.q.id;
      var answered = state.answers[qId];
      if (answered === undefined) return;
      var deficit = 3 - answered;
      if (deficit >= 2) {
        var meta = ACCELERATOR_LABELS[qId];
        if (meta) acceleratorItems.push({ label: meta.label, deficit: deficit });
      }
    });
    acceleratorItems.sort(function (a, b) { return b.deficit - a.deficit; });

    state.result = {
      chronologicalAge: age,
      biologicalAge: Math.max(18, age + offset),
      offset: offset,
      totalScore: total,
      maxScore: 57,
      tier: tier,
      categoryScores: catScores,
      weakestCategories: weakest,
      accelerators: acceleratorItems.slice(0, 5).map(function (i) { return i.label; })
    };
  }

  // --- Results ---
  function showResults() {
    calculateResult();
    showScreen('results');
    var r = state.result;

    // Gauge
    renderGauge(r);

    // Age counter
    var numEl = qs('.ba-bio-num');
    if (numEl) {
      numEl.style.color = TIER_COLORS[r.tier];
      if (window.gsap) {
        var obj = { val: r.chronologicalAge };
        gsap.to(obj, {
          val: r.biologicalAge, duration: 2, ease: 'power2.out',
          onUpdate: function () { numEl.textContent = Math.round(obj.val); }
        });
      } else {
        numEl.textContent = r.biologicalAge;
      }
    }

    // Chrono
    var chronEl = qs('.ba-chrono__num');
    if (chronEl) chronEl.textContent = r.chronologicalAge;

    // Offset text
    var offEl = qs('.ba-offset-text');
    if (offEl) {
      offEl.style.color = TIER_COLORS[r.tier];
      var diff = Math.abs(r.offset);
      if (r.offset < 0) offEl.textContent = 'Your body appears to be aging ' + diff + ' years slower than expected.';
      else if (r.offset === 0) offEl.textContent = 'Your biological and chronological age match.';
      else offEl.textContent = 'Your body appears to be aging ' + diff + ' years faster than expected.';
    }

    // Tier
    var badge = qs('.ba-tier-badge');
    if (badge) { badge.className = 'ba-tier-badge ba-tier--' + r.tier; badge.textContent = TIER_LABELS[r.tier]; }
    var msg = qs('.ba-tier-msg');
    if (msg) msg.textContent = TIER_MSGS[r.tier];

    // Save progress and render progress tracker
    saveProgress(r);
    renderProgress(r);

    // Personalized narrative
    renderNarrative(r);

    // New enhanced results blocks
    renderProjection(r);
    renderBiomarkers(r);
    renderAccelerators(r);

    // Bars (SVG rects)
    renderBars(r);

    // Impact-ranked actions with embedded free content
    renderActions(r);

    // Share
    initShare(r);
  }

  function renderGauge(r) {
    var wrap = qs('.ba-gauge-wrap');
    if (!wrap) return;
    var sz = 240, rad = 100, cx = sz / 2, cy = sz / 2;
    var circ = 2 * Math.PI * rad;
    var arc = circ * 0.75, gap = circ - arc;
    var mn = r.chronologicalAge - 20, mx = r.chronologicalAge + 20;
    var clamped = Math.min(mx, Math.max(mn, r.biologicalAge));
    var pct = 1 - ((clamped - mn) / (mx - mn));
    var fill = arc * Math.max(0.05, pct);
    var col = TIER_COLORS[r.tier];

    var existing = wrap.querySelector('svg');
    if (existing) existing.remove();

    wrap.insertAdjacentHTML('afterbegin',
      '<svg class="ba-gauge-svg" viewBox="0 0 ' + sz + ' ' + sz + '">'
      + '<circle class="ba-gauge__track" cx="' + cx + '" cy="' + cy + '" r="' + rad + '" stroke-dasharray="' + arc + ' ' + gap + '" transform="rotate(135 ' + cx + ' ' + cy + ')"/>'
      + '<circle class="ba-gauge__fill" cx="' + cx + '" cy="' + cy + '" r="' + rad + '" stroke="' + col + '" stroke-dasharray="' + fill + ' ' + (circ - fill) + '" transform="rotate(135 ' + cx + ' ' + cy + ')" style="stroke-dashoffset:' + fill + '"/>'
      + '</svg>');

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var f = wrap.querySelector('.ba-gauge__fill');
        if (f) f.style.strokeDashoffset = '0';
      });
    });
  }

  function renderBars(r) {
    var container = qs('.ba-bars');
    if (!container) return;
    var html = '';
    var barColors = { optimal: '#2ECC71', good: '#D3A23C', mid: '#E67E22', low: '#E74C3C' };

    CATEGORIES.forEach(function (cat, i) {
      var d = r.categoryScores[cat.id];
      var pct = d.percent;
      var col = pct >= 70 ? barColors.optimal : pct >= 50 ? barColors.good : pct >= 30 ? barColors.mid : barColors.low;

      html += '<div class="ba-bar-row">'
        + '<div class="ba-bar-hdr"><span class="ba-bar-name"><span>' + cat.emoji + '</span> ' + cat.label + '</span><span class="ba-bar-val">' + d.score + '/' + d.max + '</span></div>'
        + '<svg width="100%" height="12" style="display:block;" role="img" aria-label="' + cat.label + ' score ' + d.score + ' out of ' + d.max + '">'
        + '<rect x="0" y="1" width="100%" height="10" rx="5" fill="rgba(255,255,255,0.06)"/>'
        + '<rect class="ba-bar-fill" x="0" y="1" width="0%" height="10" rx="5" fill="' + col + '" data-target="' + pct + '"/>'
        + '</svg></div>';
    });

    container.innerHTML = html;

    // Animate bars
    setTimeout(function () {
      container.querySelectorAll('.ba-bar-fill').forEach(function (el, i) {
        var target = el.getAttribute('data-target') + '%';
        if (window.gsap) {
          gsap.to(el, { attr: { width: target }, duration: 0.8, delay: i * 0.15, ease: 'power2.out' });
        } else {
          el.setAttribute('width', target);
        }
      });
    }, 200);
  }

  function renderProjection(r) {
    var nowEl = qs('.ba-projection__now');
    var optEl = qs('.ba-projection__opt');
    if (!nowEl || !optEl) return;

    var trajectory = r.biologicalAge + 5;
    var saving = Math.max(2, Math.round(Math.abs(r.offset) * 0.4));
    var optimized = Math.max(r.chronologicalAge + 1, trajectory - saving);

    nowEl.textContent = trajectory;
    nowEl.style.color = TIER_COLORS[r.tier];
    optEl.textContent = optimized;

    if (window.gsap) {
      gsap.fromTo(qs('.ba-projection'), { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.6, ease: 'power2.out' });
    }
  }

  function renderBiomarkers(r) {
    var container = qs('.ba-biomarkers__list');
    if (!container) return;
    var html = '';
    var a = state.answers;
    var order = ['inflammation', 'cognitive', 'stress', 'energy', 'lifestyle'];
    order.forEach(function (catId) {
      var score = r.categoryScores[catId];
      if (!score) return;
      var level = score.percent >= 67 ? 'high' : score.percent >= 34 ? 'mid' : 'low';
      var markers = BIOMARKER_PROFILES[catId][level];
      var statusClass = level === 'high' ? 'is-ok' : level === 'mid' ? 'is-mid' : 'is-flagged';
      markers.forEach(function (marker) {
        var evidenceFn = BIOMARKER_EVIDENCE[marker];
        var evidence = evidenceFn ? evidenceFn(a) : null;
        var whyBtn = evidence ? '<button type="button" class="ba-biomarker__why" aria-expanded="false">Why?</button>' : '';
        var whyPanel = evidence ? '<div class="ba-biomarker__evidence">' + evidence + '</div>' : '';
        html += '<div class="ba-biomarker ' + statusClass + '">'
          + '<div class="ba-biomarker__row">'
          + '<span class="ba-biomarker__dot"></span>'
          + '<span class="ba-biomarker__name">' + marker + '</span>'
          + whyBtn
          + '</div>'
          + whyPanel
          + '</div>';
      });
    });
    container.innerHTML = html;

    // Bind Why? toggles
    container.querySelectorAll('.ba-biomarker__why').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var parent = this.closest('.ba-biomarker');
        var panel = parent.querySelector('.ba-biomarker__evidence');
        var expanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', !expanded);
        this.textContent = expanded ? 'Why?' : 'Hide';
        if (panel) {
          panel.style.display = expanded ? 'none' : 'block';
          if (!expanded && window.gsap) {
            gsap.fromTo(panel, { opacity: 0, height: 0 }, { opacity: 1, height: 'auto', duration: 0.3, ease: 'power2.out' });
          }
        }
      });
    });

    if (window.gsap) {
      gsap.fromTo(container.querySelectorAll('.ba-biomarker'),
        { opacity: 0, x: -10 },
        { opacity: 1, x: 0, duration: 0.35, stagger: 0.06, delay: 0.8, ease: 'power2.out' }
      );
    }
  }

  function renderAccelerators(r) {
    var container = qs('.ba-accelerators__list');
    if (!container) return;

    var items = [];
    ALL_QUESTIONS.forEach(function (item) {
      var qId = item.q.id;
      var answered = state.answers[qId];
      if (answered === undefined) return;
      var deficit = 3 - answered;
      if (deficit >= 2) {
        var meta = ACCELERATOR_LABELS[qId];
        if (meta) items.push({ label: meta.label, deficit: deficit });
      }
    });
    items.sort(function (a, b) { return b.deficit - a.deficit; });

    var html = '';
    items.slice(0, 5).forEach(function (item, i) {
      var impactClass = item.deficit === 3 ? 'is-high' : 'is-mid';
      html += '<div class="ba-accel-item ' + impactClass + '">'
        + '<span class="ba-accel-item__rank">' + (i + 1) + '</span>'
        + '<span class="ba-accel-item__label">' + item.label + '</span>'
        + '<span class="ba-accel-item__impact">' + (item.deficit === 3 ? 'High impact' : 'Moderate impact') + '</span>'
        + '</div>';
    });

    if (!html) {
      container.innerHTML = '<p class="ba-accel-empty">No major accelerators detected. Your patterns look strong.</p>';
      return;
    }
    container.innerHTML = html;

    if (window.gsap) {
      gsap.fromTo(container.querySelectorAll('.ba-accel-item'),
        { opacity: 0, x: 16 },
        { opacity: 1, x: 0, duration: 0.4, stagger: 0.1, delay: 1.0, ease: 'power2.out' }
      );
    }
  }

  function renderActions(r) {
    var container = qs('.ba-actions-list');
    if (!container) return;
    container.innerHTML = '';

    // Sort categories by deficit (worst first), only show those below 70%
    var ranked = Object.keys(r.categoryScores)
      .map(function (catId) { return { catId: catId, pct: r.categoryScores[catId].percent }; })
      .filter(function (c) { return c.pct < 70; })
      .sort(function (a, b) { return a.pct - b.pct; });

    if (ranked.length === 0) {
      container.innerHTML = '<p class="ba-actions-empty">Your scores are strong across all categories. Focus on maintaining your current habits and optimizing where you can.</p>';
      return;
    }

    ranked.forEach(function (item, i) {
      var rec = RECOMMENDATIONS[item.catId];
      if (!rec) return;
      var deficit = 100 - item.pct;
      var yearEst = (rec.yearImpact * (deficit / 100) * 2).toFixed(1);
      if (parseFloat(yearEst) < 0.3) yearEst = '0.3';

      // Free action with full embedded content
      var freeHtml = '';
      if (rec.freeContent) {
        freeHtml = '<div class="ba-action__free">'
          + '<span class="ba-action__free-badge">FREE ACTION</span>'
          + '<div class="ba-action__free-content">' + rec.freeContent + '</div>'
          + '</div>';
      }

      // Supplement recommendation
      var supplementHtml = '';
      if (rec.product) {
        supplementHtml = '<div class="ba-action__supplement">'
          + '<span class="ba-action__supp-badge">TARGETED SUPPORT</span>'
          + '<span>' + rec.body + '</span>'
          + '<a href="' + rec.ctaUrl + '" class="ba-action__supp-link">' + rec.cta + '</a>'
          + '<p class="ba-action__product">' + rec.product + '</p>'
          + '</div>';
      }

      container.innerHTML += '<div class="ba-action" data-action-cat="' + item.catId + '">'
        + '<div class="ba-action__hdr">'
        + '<span class="ba-action__rank">#' + (i + 1) + '</span>'
        + '<span class="ba-action__emoji">' + rec.emoji + '</span>'
        + '<h4 class="ba-action__title">' + rec.title + '</h4>'
        + '<span class="ba-action__impact">-' + yearEst + ' yrs</span>'
        + '</div>'
        + freeHtml
        + supplementHtml
        + '</div>';
    });

    // Init breathing exercise if stress action was rendered
    initBreathingExercise();

    if (window.gsap) {
      gsap.fromTo(container.querySelectorAll('.ba-action'), { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.5, stagger: 0.2, delay: 1.2, ease: 'power2.out' });
    }
  }

  function renderNarrative(r) {
    var container = qs('.ba-narrative');
    if (!container) return;
    var text = generateNarrative(r);
    container.innerHTML = '<p>' + text + '</p>';
    if (window.gsap) {
      gsap.fromTo(container, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.6, delay: 0.3, ease: 'power2.out' });
    }
  }

  function initBreathingExercise() {
    var toggle = qs('.ba-breathe__toggle');
    if (!toggle) return;
    var circle = qs('.ba-breathe__circle');
    var textEl = qs('.ba-breathe__text');
    var running = false;
    var timer = null;
    var cycle = 0;
    var maxCycles = 4;

    function runCycle() {
      if (cycle >= maxCycles) { stopBreathing(); return; }
      // Inhale 4s
      circle.className = 'ba-breathe__circle ba-breathe--inhale';
      textEl.textContent = 'Inhale...';
      timer = setTimeout(function () {
        // Hold 7s
        circle.className = 'ba-breathe__circle ba-breathe--hold';
        textEl.textContent = 'Hold...';
        timer = setTimeout(function () {
          // Exhale 8s
          circle.className = 'ba-breathe__circle ba-breathe--exhale';
          textEl.textContent = 'Exhale...';
          timer = setTimeout(function () {
            cycle++;
            if (cycle < maxCycles) runCycle();
            else stopBreathing();
          }, 8000);
        }, 7000);
      }, 4000);
    }

    function stopBreathing() {
      running = false;
      clearTimeout(timer);
      circle.className = 'ba-breathe__circle';
      textEl.textContent = cycle >= maxCycles ? 'Complete!' : 'Tap to start';
      toggle.textContent = 'Start Breathing Exercise';
      cycle = 0;
      if (cycle >= maxCycles) {
        setTimeout(function () { textEl.textContent = 'Tap to start'; }, 2000);
      }
    }

    toggle.addEventListener('click', function () {
      if (running) { stopBreathing(); }
      else { running = true; cycle = 0; toggle.textContent = 'Stop'; runCycle(); }
    });
  }

  // --- Progress Tracking (localStorage) ---
  var STORAGE_KEY = 'ba_history';

  function saveProgress(r) {
    try {
      var history = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      history.push({
        date: new Date().toISOString().split('T')[0],
        bioAge: r.biologicalAge,
        chronoAge: r.chronologicalAge,
        offset: r.offset,
        tier: r.tier,
        scores: {},
        totalScore: r.totalScore
      });
      // Store per-category scores
      Object.keys(r.categoryScores).forEach(function (k) {
        history[history.length - 1].scores[k] = r.categoryScores[k].score;
      });
      // Keep last 10 entries
      if (history.length > 10) history = history.slice(-10);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } catch (e) { /* localStorage unavailable */ }
  }

  function getHistory() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function renderProgress(r) {
    var container = qs('.ba-progress-track');
    if (!container) return;
    var history = getHistory();
    // Only show if there's at least one previous entry (before the current one we just saved)
    if (history.length < 2) { container.style.display = 'none'; return; }

    var prev = history[history.length - 2];
    var delta = r.biologicalAge - prev.bioAge;
    var deltaText, deltaClass;
    if (delta < 0) { deltaText = delta + ' years'; deltaClass = 'is-improved'; }
    else if (delta === 0) { deltaText = 'No change'; deltaClass = 'is-same'; }
    else { deltaText = '+' + delta + ' years'; deltaClass = 'is-worse'; }

    container.style.display = 'block';
    container.innerHTML = '<h3 class="ba-progress-track__title">Your Progress</h3>'
      + '<div class="ba-progress-track__compare">'
      + '<div class="ba-progress-track__prev">'
      + '<span class="ba-progress-track__label">Previous</span>'
      + '<span class="ba-progress-track__age">' + prev.bioAge + '</span>'
      + '<span class="ba-progress-track__date">' + prev.date + '</span>'
      + '</div>'
      + '<div class="ba-progress-track__arrow ' + deltaClass + '">'
      + '<span class="ba-progress-track__delta">' + deltaText + '</span>'
      + '\u2192'
      + '</div>'
      + '<div class="ba-progress-track__current">'
      + '<span class="ba-progress-track__label">Now</span>'
      + '<span class="ba-progress-track__age">' + r.biologicalAge + '</span>'
      + '<span class="ba-progress-track__date">Today</span>'
      + '</div>'
      + '</div>'
      + '<p class="ba-progress-track__note">Retake in 30 days for the most meaningful comparison.</p>';

    if (window.gsap) {
      gsap.fromTo(container, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.1, ease: 'power2.out' });
    }
  }

  function initShare(r) {
    qsa('.ba-share-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var net = this.getAttribute('data-network');
        var bio = r.biologicalAge;
        var diff = Math.abs(r.offset);
        var dir = r.offset > 0 ? 'older' : 'younger';
        var text = 'I just found out my biological age is ' + bio + ' \u2014 ' + diff + ' years ' + dir + " than my actual age. What's yours?";
        var baseUrl = window.location.origin + window.location.pathname;
        var shareUrl = baseUrl + '?ref=share&bio_age=' + bio + '&chrono_age=' + r.chronologicalAge;

        if (net === 'facebook') window.open('https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(shareUrl) + '&quote=' + encodeURIComponent(text), '_blank', 'width=600,height=400');
        else if (net === 'twitter') window.open('https://twitter.com/intent/tweet?text=' + encodeURIComponent(text + ' ' + shareUrl), '_blank', 'width=600,height=400');
        else if (net === 'copy') {
          var self = this;
          if (navigator.clipboard) {
            navigator.clipboard.writeText(shareUrl).then(function () {
              self.textContent = '\u2713 Copied!';
              setTimeout(function () { self.textContent = 'Copy Link'; }, 2000);
            });
          }
        }
      });
    });
  }

  // --- Klaviyo ---
  function submitKlaviyo(email) {
    var r = state.result;
    if (!r) return;
    var _learnq = window._learnq || [];
    var a = state.answers;

    var prodMap = { cognitive: 'IgniCognition', inflammation: 'IgniLongevity', stress: 'IgniLongevity', energy: 'Bundle', lifestyle: 'Protocol' };

    // Human-readable answer labels for each question
    var answerLabels = {};
    var ANSWER_LABEL_MAP = {
      brain_fog: ['Daily', 'A few times a week', 'Rarely', 'Almost never'],
      memory: ['Frequently forget things', 'Sometimes struggle', 'Generally sharp', 'Exceptional'],
      focus_switching: ['I really struggle', 'Takes a moment', 'Pretty quick', 'Near-instant'],
      recovery_time: ['Days', 'About 24 hours', 'Same day', 'Barely notice'],
      joint_stiffness: ['Daily', 'Frequently', 'Occasionally', 'Rarely'],
      illness_frequency: ['Monthly', 'Every few months', 'Once or twice a year', 'Almost never'],
      wound_healing: ['Very slowly', 'Slower than expected', 'Normal pace', 'Very quickly'],
      stress_recovery: ['I carry it for days', 'Takes a while', 'Bounce back fairly quickly', 'Handle it well'],
      emotional_overwhelm: ['Daily', 'Several times a week', 'Occasionally', 'Rarely'],
      sleep_quality: ['Terrible', 'Inconsistent', 'Generally good', 'Deep and restorative'],
      social_connection: ['Isolated or lonely', 'A few connections', 'Good social circle', 'Deep, meaningful relationships'],
      screen_time_sleep: ['Right until I fall asleep', '30-60 minutes', 'Under 30 minutes', 'I avoid screens before bed'],
      energy_pattern: ['Exhausted by noon', 'Afternoon crash', 'Steady with some dips', 'Strong all day'],
      caffeine_dependence: ["Can't function without it", '2-3 cups daily', 'One cup, more for enjoyment', "Don't need it"],
      exercise_frequency: ['0-1 days', '2-3 days', '4-5 days', '6+ days'],
      diet_quality: ['Mostly processed', 'Mixed', 'Mostly whole foods', 'Very intentional'],
      hydration: ['Poorly', 'Could be better', 'Pretty well', 'Excellent'],
      alcohol_consumption: ['10 or more', '5 to 9', '1 to 4', 'Rarely or never'],
      nature_time: ['Almost none', '1-2 hours', '3-5 hours', '5+ hours']
    };
    Object.keys(ANSWER_LABEL_MAP).forEach(function (qid) {
      answerLabels[qid] = a[qid] !== undefined ? ANSWER_LABEL_MAP[qid][a[qid]] || '' : '';
    });

    // Offset direction
    var offsetDir = r.offset > 0 ? 'older' : r.offset < 0 ? 'younger' : 'same';

    // Category tier labels
    var catTiers = {};
    ['cognitive', 'inflammation', 'stress', 'energy', 'lifestyle'].forEach(function (cat) {
      var pct = r.categoryScores[cat].percent;
      catTiers[cat] = pct >= 80 ? 'strong' : pct >= 50 ? 'moderate' : 'flagged';
    });

    // Biomarker flags per category
    var biomarkerFlags = {};
    ['cognitive', 'inflammation', 'stress', 'energy', 'lifestyle'].forEach(function (cat) {
      var pct = r.categoryScores[cat].percent;
      var level = pct >= 80 ? 'high' : pct >= 50 ? 'mid' : 'low';
      biomarkerFlags[cat] = (BIOMARKER_PROFILES[cat] && BIOMARKER_PROFILES[cat][level]) ? BIOMARKER_PROFILES[cat][level].join(', ') : '';
    });

    // Cross-reference patterns detected
    var crossRefPatterns = [];
    Object.keys(CROSS_REFS).forEach(function (qid) {
      if (a[qid] !== undefined) {
        var result = CROSS_REFS[qid](a[qid], a);
        if (result) crossRefPatterns.push(qid);
      }
    });

    // 5-year projections
    var trajectory5yr = r.biologicalAge + 5;
    var optimized5yr = r.chronologicalAge + 5 + Math.max(0, Math.round(r.offset * 0.3));

    // Narrative
    var narrative = generateNarrative(r).substring(0, 500);

    // Recommended product for primary weakness
    var recProduct = prodMap[r.weakestCategories[0]] || 'Bundle';

    // Year impact for primary weakness
    var primaryRec = RECOMMENDATIONS[r.weakestCategories[0]];
    var primaryYearImpact = primaryRec ? primaryRec.yearImpact : 0;

    // Build risk flags: categories below 50%
    var riskFlags = [];
    ['cognitive', 'inflammation', 'stress', 'energy', 'lifestyle'].forEach(function (cat) {
      if (r.categoryScores[cat].percent < 50) riskFlags.push(cat);
    });

    var retakeNum = getHistory().length;
    var prevHistory = getHistory();
    var prevBioAge = prevHistory.length > 1 ? prevHistory[prevHistory.length - 2].bioAge : null;

    _learnq.push(['identify', {
      '$email': email,
      'App Source': 'Biological Age Quiz',

      // Core results
      'Quiz - Chronological Age': r.chronologicalAge,
      'Quiz - Biological Age': r.biologicalAge,
      'Quiz - Age Offset': r.offset,
      'Quiz - Offset Direction': offsetDir,
      'Quiz - Tier': r.tier,
      'Quiz - Total Score': r.totalScore,
      'Quiz - Max Score': r.maxScore,
      'Quiz - Score Percent': Math.round((r.totalScore / r.maxScore) * 100),

      // Category scores + percents + tiers
      'Quiz - Cognitive Score': r.categoryScores.cognitive.score,
      'Quiz - Cognitive Percent': r.categoryScores.cognitive.percent,
      'Quiz - Cognitive Tier': catTiers.cognitive,
      'Quiz - Inflammation Score': r.categoryScores.inflammation.score,
      'Quiz - Inflammation Percent': r.categoryScores.inflammation.percent,
      'Quiz - Inflammation Tier': catTiers.inflammation,
      'Quiz - Stress Score': r.categoryScores.stress.score,
      'Quiz - Stress Percent': r.categoryScores.stress.percent,
      'Quiz - Stress Tier': catTiers.stress,
      'Quiz - Energy Score': r.categoryScores.energy.score,
      'Quiz - Energy Percent': r.categoryScores.energy.percent,
      'Quiz - Energy Tier': catTiers.energy,
      'Quiz - Lifestyle Score': r.categoryScores.lifestyle.score,
      'Quiz - Lifestyle Percent': r.categoryScores.lifestyle.percent,
      'Quiz - Lifestyle Tier': catTiers.lifestyle,

      // Weaknesses + accelerators (all 5)
      'Quiz - Primary Weakness': r.weakestCategories[0],
      'Quiz - Secondary Weakness': r.weakestCategories[1] || '',
      'Quiz - Accelerator 1': r.accelerators[0] || '',
      'Quiz - Accelerator 2': r.accelerators[1] || '',
      'Quiz - Accelerator 3': r.accelerators[2] || '',
      'Quiz - Accelerator 4': r.accelerators[3] || '',
      'Quiz - Accelerator 5': r.accelerators[4] || '',
      'Quiz - Number of Accelerators': r.accelerators.length,

      // All 19 raw answers (0-3 numeric)
      'Quiz - Brain Fog': a.brain_fog !== undefined ? a.brain_fog : '',
      'Quiz - Memory': a.memory !== undefined ? a.memory : '',
      'Quiz - Focus Switching': a.focus_switching !== undefined ? a.focus_switching : '',
      'Quiz - Recovery Time': a.recovery_time !== undefined ? a.recovery_time : '',
      'Quiz - Joint Stiffness': a.joint_stiffness !== undefined ? a.joint_stiffness : '',
      'Quiz - Illness Frequency': a.illness_frequency !== undefined ? a.illness_frequency : '',
      'Quiz - Wound Healing': a.wound_healing !== undefined ? a.wound_healing : '',
      'Quiz - Stress Recovery': a.stress_recovery !== undefined ? a.stress_recovery : '',
      'Quiz - Emotional Overwhelm': a.emotional_overwhelm !== undefined ? a.emotional_overwhelm : '',
      'Quiz - Sleep Quality': a.sleep_quality !== undefined ? a.sleep_quality : '',
      'Quiz - Social Connection': a.social_connection !== undefined ? a.social_connection : '',
      'Quiz - Screen Time Before Bed': a.screen_time_sleep !== undefined ? a.screen_time_sleep : '',
      'Quiz - Energy Pattern': a.energy_pattern !== undefined ? a.energy_pattern : '',
      'Quiz - Caffeine Dependence': a.caffeine_dependence !== undefined ? a.caffeine_dependence : '',
      'Quiz - Exercise Frequency': a.exercise_frequency !== undefined ? a.exercise_frequency : '',
      'Quiz - Diet Quality': a.diet_quality !== undefined ? a.diet_quality : '',
      'Quiz - Hydration': a.hydration !== undefined ? a.hydration : '',
      'Quiz - Alcohol Consumption': a.alcohol_consumption !== undefined ? a.alcohol_consumption : '',
      'Quiz - Nature Time': a.nature_time !== undefined ? a.nature_time : '',

      // All 19 human-readable answer labels
      'Quiz - Brain Fog Label': answerLabels.brain_fog,
      'Quiz - Memory Label': answerLabels.memory,
      'Quiz - Focus Switching Label': answerLabels.focus_switching,
      'Quiz - Recovery Time Label': answerLabels.recovery_time,
      'Quiz - Joint Stiffness Label': answerLabels.joint_stiffness,
      'Quiz - Illness Frequency Label': answerLabels.illness_frequency,
      'Quiz - Wound Healing Label': answerLabels.wound_healing,
      'Quiz - Stress Recovery Label': answerLabels.stress_recovery,
      'Quiz - Emotional Overwhelm Label': answerLabels.emotional_overwhelm,
      'Quiz - Sleep Quality Label': answerLabels.sleep_quality,
      'Quiz - Social Connection Label': answerLabels.social_connection,
      'Quiz - Screen Time Before Bed Label': answerLabels.screen_time_sleep,
      'Quiz - Energy Pattern Label': answerLabels.energy_pattern,
      'Quiz - Caffeine Dependence Label': answerLabels.caffeine_dependence,
      'Quiz - Exercise Frequency Label': answerLabels.exercise_frequency,
      'Quiz - Diet Quality Label': answerLabels.diet_quality,
      'Quiz - Hydration Label': answerLabels.hydration,
      'Quiz - Alcohol Consumption Label': answerLabels.alcohol_consumption,
      'Quiz - Nature Time Label': answerLabels.nature_time,

      // Biomarker flags per category
      'Quiz - Cognitive Biomarkers': biomarkerFlags.cognitive,
      'Quiz - Inflammation Biomarkers': biomarkerFlags.inflammation,
      'Quiz - Stress Biomarkers': biomarkerFlags.stress,
      'Quiz - Energy Biomarkers': biomarkerFlags.energy,
      'Quiz - Lifestyle Biomarkers': biomarkerFlags.lifestyle,

      // Risk + cross-reference patterns
      'Quiz - Risk Flags': riskFlags.join(', '),
      'Quiz - Number of Risk Flags': riskFlags.length,
      'Quiz - Cross Reference Patterns': crossRefPatterns.join(', '),
      'Quiz - Number of Cross Refs': crossRefPatterns.length,

      // Projections
      'Quiz - 5yr Trajectory Age': trajectory5yr,
      'Quiz - 5yr Optimized Age': optimized5yr,
      'Quiz - 5yr Gap': trajectory5yr - optimized5yr,
      'Quiz - Primary Year Impact': primaryYearImpact,

      // Product recommendations
      'Quiz - Recommended Product': recProduct,
      'Quiz - Primary Rec Title': primaryRec ? primaryRec.title : '',

      // Narrative + metadata
      'Quiz - Narrative Summary': narrative,
      'Quiz - Completed Date': new Date().toISOString(),
      'Quiz - Source': new URLSearchParams(window.location.search).get('utm_source') || 'direct',
      'Quiz - UTM Medium': new URLSearchParams(window.location.search).get('utm_medium') || '',
      'Quiz - UTM Campaign': new URLSearchParams(window.location.search).get('utm_campaign') || '',
      'Quiz - Retake Number': retakeNum,
      'Quiz - Previous Bio Age': prevBioAge || '',
      'Quiz - Improvement': prevBioAge ? prevBioAge - r.biologicalAge : ''
    }]);

    _learnq.push(['track', 'Biological Age Quiz Completed', {
      'App Source': 'Biological Age Quiz',
      'Biological Age': r.biologicalAge,
      'Chronological Age': r.chronologicalAge,
      'Age Offset': r.offset,
      'Offset Direction': offsetDir,
      'Tier': r.tier,
      'Total Score': r.totalScore,
      'Score Percent': Math.round((r.totalScore / r.maxScore) * 100),

      // Category percents + tiers
      'Cognitive Percent': r.categoryScores.cognitive.percent,
      'Cognitive Tier': catTiers.cognitive,
      'Inflammation Percent': r.categoryScores.inflammation.percent,
      'Inflammation Tier': catTiers.inflammation,
      'Stress Percent': r.categoryScores.stress.percent,
      'Stress Tier': catTiers.stress,
      'Energy Percent': r.categoryScores.energy.percent,
      'Energy Tier': catTiers.energy,
      'Lifestyle Percent': r.categoryScores.lifestyle.percent,
      'Lifestyle Tier': catTiers.lifestyle,

      // Weaknesses + accelerators
      'Primary Weakness': r.weakestCategories[0],
      'Secondary Weakness': r.weakestCategories[1] || '',
      'Accelerator 1': r.accelerators[0] || '',
      'Accelerator 2': r.accelerators[1] || '',
      'Accelerator 3': r.accelerators[2] || '',
      'Number of Accelerators': r.accelerators.length,

      // Key lifestyle answers (labels for easy flow building)
      'Sleep Quality': answerLabels.sleep_quality,
      'Stress Recovery': answerLabels.stress_recovery,
      'Exercise Frequency': answerLabels.exercise_frequency,
      'Diet Quality': answerLabels.diet_quality,
      'Alcohol Consumption': answerLabels.alcohol_consumption,
      'Social Connection': answerLabels.social_connection,
      'Caffeine Dependence': answerLabels.caffeine_dependence,
      'Screen Time Before Bed': answerLabels.screen_time_sleep,

      // Biomarker + risk
      'Risk Flags': riskFlags.join(', '),
      'Number of Risk Flags': riskFlags.length,
      'Cross Reference Patterns': crossRefPatterns.join(', '),

      // Projections
      '5yr Trajectory Age': trajectory5yr,
      '5yr Optimized Age': optimized5yr,
      '5yr Gap': trajectory5yr - optimized5yr,

      // Product + narrative
      'Recommended Product': recProduct,
      'Primary Rec Title': primaryRec ? primaryRec.title : '',
      'Narrative Summary': narrative,
      'Retake Number': retakeNum,
      'Previous Bio Age': prevBioAge || '',
      'Improvement': prevBioAge ? prevBioAge - r.biologicalAge : ''
    }]);
  }

  // Fire quiz data after Klaviyo embedded form handles its own submission
  // The embedded form already identified the profile — we just attach our data
  function submitKlaviyoFromEmbed() {
    var r = state.result;
    if (!r) return;
    var _learnq = window._learnq || [];
    var a = state.answers;

    var prodMap = { cognitive: 'IgniCognition', inflammation: 'IgniLongevity', stress: 'IgniLongevity', energy: 'Bundle', lifestyle: 'Protocol' };

    var ANSWER_LABEL_MAP = {
      brain_fog: ['Daily', 'A few times a week', 'Rarely', 'Almost never'],
      memory: ['Frequently forget things', 'Sometimes struggle', 'Generally sharp', 'Exceptional'],
      focus_switching: ['I really struggle', 'Takes a moment', 'Pretty quick', 'Near-instant'],
      recovery_time: ['Days', 'About 24 hours', 'Same day', 'Barely notice'],
      joint_stiffness: ['Daily', 'Frequently', 'Occasionally', 'Rarely'],
      illness_frequency: ['Monthly', 'Every few months', 'Once or twice a year', 'Almost never'],
      wound_healing: ['Very slowly', 'Slower than expected', 'Normal pace', 'Very quickly'],
      stress_recovery: ['I carry it for days', 'Takes a while', 'Bounce back fairly quickly', 'Handle it well'],
      emotional_overwhelm: ['Daily', 'Several times a week', 'Occasionally', 'Rarely'],
      sleep_quality: ['Terrible', 'Inconsistent', 'Generally good', 'Deep and restorative'],
      social_connection: ['Isolated or lonely', 'A few connections', 'Good social circle', 'Deep, meaningful relationships'],
      screen_time_sleep: ['Right until I fall asleep', '30-60 minutes', 'Under 30 minutes', 'I avoid screens before bed'],
      energy_pattern: ['Exhausted by noon', 'Afternoon crash', 'Steady with some dips', 'Strong all day'],
      caffeine_dependence: ["Can't function without it", '2-3 cups daily', 'One cup, more for enjoyment', "Don't need it"],
      exercise_frequency: ['0-1 days', '2-3 days', '4-5 days', '6+ days'],
      diet_quality: ['Mostly processed', 'Mixed', 'Mostly whole foods', 'Very intentional'],
      hydration: ['Poorly', 'Could be better', 'Pretty well', 'Excellent'],
      alcohol_consumption: ['10 or more', '5 to 9', '1 to 4', 'Rarely or never'],
      nature_time: ['Almost none', '1-2 hours', '3-5 hours', '5+ hours']
    };
    var answerLabels = {};
    Object.keys(ANSWER_LABEL_MAP).forEach(function (qid) {
      answerLabels[qid] = a[qid] !== undefined ? ANSWER_LABEL_MAP[qid][a[qid]] || '' : '';
    });

    var offsetDir = r.offset > 0 ? 'older' : r.offset < 0 ? 'younger' : 'same';
    var catTiers = {};
    ['cognitive', 'inflammation', 'stress', 'energy', 'lifestyle'].forEach(function (cat) {
      var pct = r.categoryScores[cat].percent;
      catTiers[cat] = pct >= 80 ? 'strong' : pct >= 50 ? 'moderate' : 'flagged';
    });
    var biomarkerFlags = {};
    ['cognitive', 'inflammation', 'stress', 'energy', 'lifestyle'].forEach(function (cat) {
      var pct = r.categoryScores[cat].percent;
      var level = pct >= 80 ? 'high' : pct >= 50 ? 'mid' : 'low';
      biomarkerFlags[cat] = (BIOMARKER_PROFILES[cat] && BIOMARKER_PROFILES[cat][level]) ? BIOMARKER_PROFILES[cat][level].join(', ') : '';
    });
    var crossRefPatterns = [];
    Object.keys(CROSS_REFS).forEach(function (qid) {
      if (a[qid] !== undefined) {
        var result = CROSS_REFS[qid](a[qid], a);
        if (result) crossRefPatterns.push(qid);
      }
    });
    var trajectory5yr = r.biologicalAge + 5;
    var optimized5yr = r.chronologicalAge + 5 + Math.max(0, Math.round(r.offset * 0.3));
    var narrative = generateNarrative(r).substring(0, 500);
    var recProduct = prodMap[r.weakestCategories[0]] || 'Bundle';
    var primaryRec = RECOMMENDATIONS[r.weakestCategories[0]];
    var riskFlags = [];
    ['cognitive', 'inflammation', 'stress', 'energy', 'lifestyle'].forEach(function (cat) {
      if (r.categoryScores[cat].percent < 50) riskFlags.push(cat);
    });
    var retakeNum = getHistory().length;
    var prevHistory = getHistory();
    var prevBioAge = prevHistory.length > 1 ? prevHistory[prevHistory.length - 2].bioAge : null;

    // Identify adds quiz properties to the profile the embedded form just created
    _learnq.push(['identify', {
      'App Source': 'Biological Age Quiz',
      'Quiz - Chronological Age': r.chronologicalAge,
      'Quiz - Biological Age': r.biologicalAge,
      'Quiz - Age Offset': r.offset,
      'Quiz - Offset Direction': offsetDir,
      'Quiz - Tier': r.tier,
      'Quiz - Total Score': r.totalScore,
      'Quiz - Max Score': r.maxScore,
      'Quiz - Score Percent': Math.round((r.totalScore / r.maxScore) * 100),
      'Quiz - Cognitive Score': r.categoryScores.cognitive.score,
      'Quiz - Cognitive Percent': r.categoryScores.cognitive.percent,
      'Quiz - Cognitive Tier': catTiers.cognitive,
      'Quiz - Inflammation Score': r.categoryScores.inflammation.score,
      'Quiz - Inflammation Percent': r.categoryScores.inflammation.percent,
      'Quiz - Inflammation Tier': catTiers.inflammation,
      'Quiz - Stress Score': r.categoryScores.stress.score,
      'Quiz - Stress Percent': r.categoryScores.stress.percent,
      'Quiz - Stress Tier': catTiers.stress,
      'Quiz - Energy Score': r.categoryScores.energy.score,
      'Quiz - Energy Percent': r.categoryScores.energy.percent,
      'Quiz - Energy Tier': catTiers.energy,
      'Quiz - Lifestyle Score': r.categoryScores.lifestyle.score,
      'Quiz - Lifestyle Percent': r.categoryScores.lifestyle.percent,
      'Quiz - Lifestyle Tier': catTiers.lifestyle,
      'Quiz - Primary Weakness': r.weakestCategories[0],
      'Quiz - Secondary Weakness': r.weakestCategories[1] || '',
      'Quiz - Accelerator 1': r.accelerators[0] || '',
      'Quiz - Accelerator 2': r.accelerators[1] || '',
      'Quiz - Accelerator 3': r.accelerators[2] || '',
      'Quiz - Accelerator 4': r.accelerators[3] || '',
      'Quiz - Accelerator 5': r.accelerators[4] || '',
      'Quiz - Number of Accelerators': r.accelerators.length,
      'Quiz - Brain Fog': a.brain_fog !== undefined ? a.brain_fog : '',
      'Quiz - Memory': a.memory !== undefined ? a.memory : '',
      'Quiz - Focus Switching': a.focus_switching !== undefined ? a.focus_switching : '',
      'Quiz - Recovery Time': a.recovery_time !== undefined ? a.recovery_time : '',
      'Quiz - Joint Stiffness': a.joint_stiffness !== undefined ? a.joint_stiffness : '',
      'Quiz - Illness Frequency': a.illness_frequency !== undefined ? a.illness_frequency : '',
      'Quiz - Wound Healing': a.wound_healing !== undefined ? a.wound_healing : '',
      'Quiz - Stress Recovery': a.stress_recovery !== undefined ? a.stress_recovery : '',
      'Quiz - Emotional Overwhelm': a.emotional_overwhelm !== undefined ? a.emotional_overwhelm : '',
      'Quiz - Sleep Quality': a.sleep_quality !== undefined ? a.sleep_quality : '',
      'Quiz - Social Connection': a.social_connection !== undefined ? a.social_connection : '',
      'Quiz - Screen Time Before Bed': a.screen_time_sleep !== undefined ? a.screen_time_sleep : '',
      'Quiz - Energy Pattern': a.energy_pattern !== undefined ? a.energy_pattern : '',
      'Quiz - Caffeine Dependence': a.caffeine_dependence !== undefined ? a.caffeine_dependence : '',
      'Quiz - Exercise Frequency': a.exercise_frequency !== undefined ? a.exercise_frequency : '',
      'Quiz - Diet Quality': a.diet_quality !== undefined ? a.diet_quality : '',
      'Quiz - Hydration': a.hydration !== undefined ? a.hydration : '',
      'Quiz - Alcohol Consumption': a.alcohol_consumption !== undefined ? a.alcohol_consumption : '',
      'Quiz - Nature Time': a.nature_time !== undefined ? a.nature_time : '',
      'Quiz - Brain Fog Label': answerLabels.brain_fog,
      'Quiz - Memory Label': answerLabels.memory,
      'Quiz - Focus Switching Label': answerLabels.focus_switching,
      'Quiz - Recovery Time Label': answerLabels.recovery_time,
      'Quiz - Joint Stiffness Label': answerLabels.joint_stiffness,
      'Quiz - Illness Frequency Label': answerLabels.illness_frequency,
      'Quiz - Wound Healing Label': answerLabels.wound_healing,
      'Quiz - Stress Recovery Label': answerLabels.stress_recovery,
      'Quiz - Emotional Overwhelm Label': answerLabels.emotional_overwhelm,
      'Quiz - Sleep Quality Label': answerLabels.sleep_quality,
      'Quiz - Social Connection Label': answerLabels.social_connection,
      'Quiz - Screen Time Before Bed Label': answerLabels.screen_time_sleep,
      'Quiz - Energy Pattern Label': answerLabels.energy_pattern,
      'Quiz - Caffeine Dependence Label': answerLabels.caffeine_dependence,
      'Quiz - Exercise Frequency Label': answerLabels.exercise_frequency,
      'Quiz - Diet Quality Label': answerLabels.diet_quality,
      'Quiz - Hydration Label': answerLabels.hydration,
      'Quiz - Alcohol Consumption Label': answerLabels.alcohol_consumption,
      'Quiz - Nature Time Label': answerLabels.nature_time,
      'Quiz - Cognitive Biomarkers': biomarkerFlags.cognitive,
      'Quiz - Inflammation Biomarkers': biomarkerFlags.inflammation,
      'Quiz - Stress Biomarkers': biomarkerFlags.stress,
      'Quiz - Energy Biomarkers': biomarkerFlags.energy,
      'Quiz - Lifestyle Biomarkers': biomarkerFlags.lifestyle,
      'Quiz - Risk Flags': riskFlags.join(', '),
      'Quiz - Number of Risk Flags': riskFlags.length,
      'Quiz - Cross Reference Patterns': crossRefPatterns.join(', '),
      'Quiz - Number of Cross Refs': crossRefPatterns.length,
      'Quiz - 5yr Trajectory Age': trajectory5yr,
      'Quiz - 5yr Optimized Age': optimized5yr,
      'Quiz - 5yr Gap': trajectory5yr - optimized5yr,
      'Quiz - Primary Year Impact': primaryRec ? primaryRec.yearImpact : 0,
      'Quiz - Recommended Product': recProduct,
      'Quiz - Primary Rec Title': primaryRec ? primaryRec.title : '',
      'Quiz - Narrative Summary': narrative,
      'Quiz - Completed Date': new Date().toISOString(),
      'Quiz - Source': new URLSearchParams(window.location.search).get('utm_source') || 'direct',
      'Quiz - UTM Medium': new URLSearchParams(window.location.search).get('utm_medium') || '',
      'Quiz - UTM Campaign': new URLSearchParams(window.location.search).get('utm_campaign') || '',
      'Quiz - Retake Number': retakeNum,
      'Quiz - Previous Bio Age': prevBioAge || '',
      'Quiz - Improvement': prevBioAge ? prevBioAge - r.biologicalAge : ''
    }]);

    _learnq.push(['track', 'Biological Age Quiz Completed', {
      'App Source': 'Biological Age Quiz',
      'Biological Age': r.biologicalAge,
      'Chronological Age': r.chronologicalAge,
      'Age Offset': r.offset,
      'Offset Direction': offsetDir,
      'Tier': r.tier,
      'Total Score': r.totalScore,
      'Score Percent': Math.round((r.totalScore / r.maxScore) * 100),
      'Cognitive Percent': r.categoryScores.cognitive.percent,
      'Cognitive Tier': catTiers.cognitive,
      'Inflammation Percent': r.categoryScores.inflammation.percent,
      'Inflammation Tier': catTiers.inflammation,
      'Stress Percent': r.categoryScores.stress.percent,
      'Stress Tier': catTiers.stress,
      'Energy Percent': r.categoryScores.energy.percent,
      'Energy Tier': catTiers.energy,
      'Lifestyle Percent': r.categoryScores.lifestyle.percent,
      'Lifestyle Tier': catTiers.lifestyle,
      'Primary Weakness': r.weakestCategories[0],
      'Secondary Weakness': r.weakestCategories[1] || '',
      'Accelerator 1': r.accelerators[0] || '',
      'Accelerator 2': r.accelerators[1] || '',
      'Accelerator 3': r.accelerators[2] || '',
      'Number of Accelerators': r.accelerators.length,
      'Sleep Quality': answerLabels.sleep_quality,
      'Stress Recovery': answerLabels.stress_recovery,
      'Exercise Frequency': answerLabels.exercise_frequency,
      'Diet Quality': answerLabels.diet_quality,
      'Alcohol Consumption': answerLabels.alcohol_consumption,
      'Social Connection': answerLabels.social_connection,
      'Caffeine Dependence': answerLabels.caffeine_dependence,
      'Screen Time Before Bed': answerLabels.screen_time_sleep,
      'Risk Flags': riskFlags.join(', '),
      'Number of Risk Flags': riskFlags.length,
      'Cross Reference Patterns': crossRefPatterns.join(', '),
      '5yr Trajectory Age': trajectory5yr,
      '5yr Optimized Age': optimized5yr,
      '5yr Gap': trajectory5yr - optimized5yr,
      'Recommended Product': recProduct,
      'Primary Rec Title': primaryRec ? primaryRec.title : '',
      'Narrative Summary': narrative,
      'Retake Number': retakeNum,
      'Previous Bio Age': prevBioAge || '',
      'Improvement': prevBioAge ? prevBioAge - r.biologicalAge : ''
    }]);
  }

  // --- Init ---
  document.addEventListener('DOMContentLoaded', function () {
    root = document.querySelector('.biofield-assessment');
    if (!root) return;

    // Check referral
    var params = new URLSearchParams(window.location.search);
    if (params.get('ref') === 'share') {
      var refBanner = qs('.ba-referral');
      if (refBanner) refBanner.classList.add('is-visible');
    }

    // Consent checkbox enables start button
    var consentBox = document.getElementById('ba-consent-agree');
    var startBtn = qs('.ba-btn--start');
    if (consentBox && startBtn) {
      consentBox.addEventListener('change', function () {
        startBtn.disabled = !this.checked;
      });
      startBtn.addEventListener('click', function () { showScreen('age'); });
    }

    // Age screen
    var ageInput = qs('.ba-age-input');
    var ageNext = qs('.ba-age-next');
    if (ageInput && ageNext) {
      ageInput.addEventListener('input', function () {
        var val = parseInt(this.value, 10);
        if (val >= 18 && val <= 99) {
          state.age = val;
          ageNext.disabled = false;
        } else {
          state.age = null;
          ageNext.disabled = true;
        }
      });
      ageInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && state.age) {
          e.preventDefault();
          state.qi = 0;
          renderQuestion(0, 'forward');
        }
      });
      ageNext.addEventListener('click', function () {
        if (state.age) {
          state.qi = 0;
          renderQuestion(0, 'forward');
        }
      });
    }

    // Email form (inline fallback)
    var emailForm = qs('.ba-email-form');
    if (emailForm) {
      emailForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = emailForm.querySelector('input[type="email"]');
        var email = input ? input.value.trim() : '';
        if (!email) return;

        submitKlaviyo(email);

        emailForm.style.display = 'none';
        var success = qs('.ba-email-success');
        if (success) success.classList.add('is-visible');
      });
    }

    // Klaviyo embedded form — fire quiz data when form is submitted
    var klaviyoFormWrap = root.querySelector('[class*="klaviyo-form-"]');
    if (klaviyoFormWrap) {
      var baKlaviyoFired = false;

      function fireKlaviyoQuizData() {
        if (baKlaviyoFired) return;
        baKlaviyoFired = true;
        // Try to grab email from the form before Klaviyo clears it
        var emailInput = klaviyoFormWrap.querySelector('input[type="email"]');
        var email = emailInput ? emailInput.value.trim() : '';
        if (email) {
          submitKlaviyo(email);
        } else {
          submitKlaviyoFromEmbed();
        }
      }

      // Method 1: Intercept click on Klaviyo's submit button
      klaviyoFormWrap.addEventListener('click', function (e) {
        var btn = e.target.closest('button[type="button"], button[type="submit"], button');
        if (!btn) return;
        var emailInput = klaviyoFormWrap.querySelector('input[type="email"]');
        if (emailInput && emailInput.value && emailInput.value.indexOf('@') > -1) {
          // Small delay to let Klaviyo process the submission first
          setTimeout(fireKlaviyoQuizData, 1500);
        }
      }, true);

      // Method 2: Listen for Enter key on email input
      klaviyoFormWrap.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          var emailInput = klaviyoFormWrap.querySelector('input[type="email"]');
          if (emailInput && emailInput.value && emailInput.value.indexOf('@') > -1) {
            setTimeout(fireKlaviyoQuizData, 1500);
          }
        }
      }, true);

      // Method 3: MutationObserver as fallback — detect form disappearing (success state)
      var klaviyoObserver = new MutationObserver(function () {
        var form = klaviyoFormWrap.querySelector('form');
        var emailInput = klaviyoFormWrap.querySelector('input[type="email"]');
        // If the form or email input disappeared, Klaviyo moved to success state
        if (!form || !emailInput) {
          fireKlaviyoQuizData();
          klaviyoObserver.disconnect();
        }
      });
      klaviyoObserver.observe(klaviyoFormWrap, { childList: true, subtree: true });
    }

    // Detect returning user
    var history = getHistory();
    if (history.length > 0) {
      var lastEntry = history[history.length - 1];
      var returnBanner = qs('.ba-intro__return');
      if (returnBanner) {
        returnBanner.style.display = 'block';
        returnBanner.innerHTML = 'Welcome back! Last assessment: <strong>biological age ' + lastEntry.bioAge + '</strong> on ' + lastEntry.date + '. Retake to see your progress.';
      }
    }

    // Show intro
    showScreen('intro');

    // GSAP intro stagger
    if (window.gsap) {
      var introEls = root.querySelectorAll('.ba-intro > *');
      gsap.fromTo(introEls, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.5, stagger: 0.1, ease: 'power2.out' });
    }
  });

})();
